<template>
  <div class="dashboard-page">
    <header class="dash-topbar">
      <div ref="topMenu" class="dash-topbar-actions">
        <button type="button" class="dash-menu-btn" aria-label="Menu" @click.stop="menuOpen = !menuOpen">
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" fill="currentColor" />
          </svg>
        </button>
        <ul v-show="menuOpen" class="dash-menu-dropdown">
          <li><router-link to="/userlogin" @click.native="menuOpen = false">Home</router-link></li>
          <li>
            <a
              href="#"
              :class="{ 'is-locked-link': isSectionLocked('questions') }"
              @click.prevent="goSection('questions', '/mlalistselection')"
            >Questions <img v-if="isSectionLocked('questions')" src="/images/lock.png" class="dash-lock-icon" alt="" /></a>
          </li>
          <li>
            <a
              href="#"
              :class="{ 'is-locked-link': isSectionLocked('notes') }"
              @click.prevent="goSection('notes', '/noteslistselection')"
            >Notes <img v-if="isSectionLocked('notes')" src="/images/lock.png" class="dash-lock-icon" alt="" /></a>
          </li>
          <li>
            <a
              href="#"
              :class="{ 'is-locked-link': isSectionLocked('mocks') }"
              @click.prevent="goSection('mocks', '/mocksection')"
            >Mocks <img v-if="isSectionLocked('mocks')" src="/images/lock.png" class="dash-lock-icon" alt="" /></a>
          </li>
          <li><a href="#" @click.prevent="logout">Logout</a></li>
        </ul>
      </div>
      <router-link to="/userlogin" class="dash-topbar-logo">
        <img src="/images/logo.png" alt="Synapse" />
      </router-link>
    </header>

    <div class="dashboard-shell">
      <!-- Sidebar column -->
      <div class="dash-sidebar-col">
        <aside class="dash-sidebar">
          <div class="dash-profile-card">
            <div class="dash-avatar-wrap">
              <div class="dash-avatar-ring" :style="{ '--p': overallProgress + '%' }">
                <div class="dash-avatar" :class="{ 'has-photo': !!profileAvatarUrl }">
                  <img
                    v-if="profileAvatarUrl"
                    :src="profileAvatarUrl"
                    alt=""
                    class="dash-avatar-img"
                  />
                  <template v-else>{{ userInitials }}</template>
                </div>
              </div>
              <button
                type="button"
                class="dash-avatar-edit"
                title="Change profile picture"
                aria-label="Change profile picture"
                :disabled="profileUploading"
                @click="triggerProfilePick"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z"
                  />
                </svg>
              </button>
              <input
                ref="profileFileInput"
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                class="dash-avatar-file"
                @change="onProfileFileSelected"
              />
            </div>
            <p v-if="profileUploadError" class="dash-avatar-error">{{ profileUploadError }}</p>
            <p class="dash-welcome">Welcome!</p>
            <h2 class="dash-name">{{ displayName }}</h2>
            <span class="dash-badge">{{ overallProgress }}% overall progress</span>
            <div class="dash-quick-icons">
              <button
                type="button"
                class="dash-icon-btn"
                :title="isSectionLocked('questions') ? 'Questions (Locked)' : 'Questions'"
                @click="goSection('questions', '/mlalistselection')"
              >
                <span class="dot dot-q"></span>
                <img v-if="isSectionLocked('questions')" src="/images/lock.png" class="dash-lock-icon-sm" alt="" />
              </button>
              <button
                type="button"
                class="dash-icon-btn"
                :title="isSectionLocked('notes') ? 'Notes (Locked)' : 'Notes'"
                @click="goSection('notes', '/noteslistselection')"
              >
                <span class="dot dot-n"></span>
                <img v-if="isSectionLocked('notes')" src="/images/lock.png" class="dash-lock-icon-sm" alt="" />
              </button>
              <button
                type="button"
                class="dash-icon-btn"
                :title="isSectionLocked('mocks') ? 'Mocks (Locked)' : 'Mocks'"
                @click="goSection('mocks', '/mocksection')"
              >
                <span class="dot dot-m"></span>
                <img v-if="isSectionLocked('mocks')" src="/images/lock.png" class="dash-lock-icon-sm" alt="" />
              </button>
            </div>
          </div>

          <nav class="dash-nav">
            <button
              v-for="item in navItems"
              :key="item.key"
              type="button"
              class="dash-nav-item"
              :class="{ active: activeSection === item.key }"
              @click="activeSection = item.key"
            >
              <span class="dash-nav-icon" aria-hidden="true" v-html="item.icon"></span>
              {{ item.label }}
            </button>
          </nav>
          <div class="dash-exam-widget">
          <div class="dash-exam-banner" :class="{ open: examPanelOpen }">
            <button type="button" class="dash-exam-banner-main" @click="toggleExamPanel">
              <svg class="dash-exam-inline-cal" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M19 4h-1V2h-2v2H8V2H6v2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z"
                />
              </svg>
              <span :class="{ 'is-exam-urgent': isExamUrgent && !examPanelOpen }">{{ examPanelOpen ? 'Hide exam date' : examBannerLabel }}</span>
            </button>
            <div class="dash-exam-cal-btn" title="Set examination date">
              <svg class="dash-exam-cal-icon" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M19 4h-1V2h-2v2H8V2H6v2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z"
                />
              </svg>
              <input
                ref="examDateInput"
                type="date"
                class="dash-exam-date-input-overlay"
                :value="examDateIso || ''"
                aria-label="Set examination date"
                @input="onExamDateChange"
                @change="onExamDateChange"
              />
            </div>
          </div>
          <transition name="dash-exam-expand">
            <div v-if="examPanelOpen" class="dash-exam-countdown">
              <div class="dash-exam-date-line" :class="{ 'is-exam-urgent': isExamUrgent }">
                {{ examDateIso ? examBannerLabel : 'Exam date — Not set' }}
              </div>
              <div class="dash-exam-count-row">
                <div class="dash-exam-count-col">
                  <span class="dash-exam-count-label">Weeks</span>
                  <span class="dash-exam-count-value">{{ examCountdown.weeks }}</span>
                </div>
                <div class="dash-exam-count-col">
                  <span class="dash-exam-count-label">Days</span>
                  <span class="dash-exam-count-value">{{ examCountdown.days }}</span>
                </div>
                <div class="dash-exam-count-col">
                  <span class="dash-exam-count-label">Hours</span>
                  <span class="dash-exam-count-value dash-exam-count-value--time">{{ examCountdown.time }}</span>
                </div>
              </div>
            </div>
          </transition>
        </div>
        </aside>

     
      </div>

      <!-- Main -->
      <main class="dash-main">
        <header class="dash-header">
          <div>
            <h1>{{ activeSectionLabel }}</h1>
          </div>
          <div
            v-if="activeSection === 'overview'"
            class="dash-metric-toggle"
            role="group"
            aria-label="Performance metric"
          >
            <button
              type="button"
              class="dash-metric-toggle__btn"
              :class="{ 'is-active': overviewMetricMode === 'progress' }"
              @click="setOverviewMetricMode('progress')"
            >
              Progress
            </button>
            <button
              type="button"
              class="dash-metric-toggle__btn"
              :class="{ 'is-active': overviewMetricMode === 'score' }"
              @click="setOverviewMetricMode('score')"
            >
              Score
            </button>
          </div>
        </header>

        <div v-if="loading && !loaded" class="dash-loading">Loading your dashboard…</div>

        <template v-else>
          <!-- Performance -->
          <section v-show="activeSection === 'overview'" class="dash-section">
            <div
              class="dash-overview-grid"
              :class="{ 'dash-overview-grid--score': isOverviewScoreMode }"
            >
              <div class="dash-section-progress-card dash-section-progress-card--q">
                <div class="mla-question-box dash-overview-progress-box">
                  <div class="section-card-body">
                    <h4>
                      <img src="/images/questionmark.png" alt="" />
                      Questions
                    </h4>
                    <HomepageProgress
                      :progress="isOverviewScoreMode ? questionProgress.scorePercent : questionProgress.percent"
                      :label="isOverviewScoreMode ? 'Score' : 'Progress'"
                      :mode="isOverviewScoreMode ? 'score' : 'progress'"
                      :detail="isOverviewScoreMode
                        ? `${questionProgress.correct}/${questionProgress.total}`
                        : `${questionProgress.attempted}/${questionProgress.total}`"
                    />
                  </div>
                  <div class="cardbottom-shadow">
                    <img src="/images/cardshadow.png" alt="" />
                  </div>
                </div>
                <div
                  class="dash-section-progress-body"
                  role="button"
                  tabindex="0"
                  title="Click to expand chart"
                  @click="openChartExpand('questions')"
                  @keydown.enter.prevent="openChartExpand('questions')"
                  @keydown.space.prevent="openChartExpand('questions')"
                >
                  <ScoreTrendChart
                    v-if="isOverviewScoreMode"
                    :key="'q-score-' + chartAnimKey"
                    title="Questions score"
                    :rows="questionsScoreRows"
                    bar-color="#1a90ff"
                    :passing-score="scorePassingPercent"
                    :average-score="scoreAveragePercent"
                  />
                  <ProgressLineChart
                    v-else
                    :key="'q-line-' + chartAnimKey"
                    title="Questions progress"
                    :weeks="progressTrendWeeks"
                    :series="questionsLineSeries"
                    tint="rgba(26, 144, 255, 0.10)"
                    :show-legend="false"
                  />
                  <button
                    type="button"
                    class="dash-section-progress-refresh"
                    title="Reset Questions scoring"
                    aria-label="Reset Questions scoring"
                    @click.stop="openSectionResetConfirm('questions')"
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                      <path fill="currentColor" d="M12 6V3L8 7l4 4V8c2.76 0 5 2.24 5 5a5 5 0 0 1-8.66 3.44l-1.42 1.42A7 7 0 0 0 19 13c0-3.87-3.13-7-7-7zm0 12v3l4-4-4-4v3a5 5 0 0 1-5-5c0-.95.27-1.83.74-2.59l-1.48-1.3A6.97 6.97 0 0 0 5 13c0 3.87 3.13 7 7 7z"/>
                    </svg>
                  </button>
                </div>
              </div>

              <div
                v-if="!isOverviewScoreMode"
                class="dash-section-progress-card dash-section-progress-card--n"
              >
                <div class="mla-question-box mla-note-box dash-overview-progress-box">
                  <div class="section-card-body">
                    <h4>
                      <img src="/images/file.png" alt="" />
                      Notes
                    </h4>
                    <HomepageProgress
                      :progress="notesProgress.percent"
                      label="Progress"
                      :detail="`${notesProgress.read}/${notesProgress.total}`"
                    />
                  </div>
                  <div class="cardbottom-shadow">
                    <img src="/images/cardshadow.png" alt="" />
                  </div>
                </div>
                <div
                  class="dash-section-progress-body"
                  role="button"
                  tabindex="0"
                  title="Click to expand chart"
                  @click="openChartExpand('notes')"
                  @keydown.enter.prevent="openChartExpand('notes')"
                  @keydown.space.prevent="openChartExpand('notes')"
                >
                  <ProgressLineChart
                    :key="'n-line-' + chartAnimKey"
                    title="Notes progress"
                    :weeks="progressTrendWeeks"
                    :series="notesLineSeries"
                    tint="rgba(245, 156, 0, 0.12)"
                    :show-legend="false"
                  />
                  <button
                    type="button"
                    class="dash-section-progress-refresh"
                    title="Reset Notes progress"
                    aria-label="Reset Notes progress"
                    @click.stop="openSectionResetConfirm('notes')"
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                      <path fill="currentColor" d="M12 6V3L8 7l4 4V8c2.76 0 5 2.24 5 5a5 5 0 0 1-8.66 3.44l-1.42 1.42A7 7 0 0 0 19 13c0-3.87-3.13-7-7-7zm0 12v3l4-4-4-4v3a5 5 0 0 1-5-5c0-.95.27-1.83.74-2.59l-1.48-1.3A6.97 6.97 0 0 0 5 13c0 3.87 3.13 7 7 7z"/>
                    </svg>
                  </button>
                </div>
              </div>

              <div class="dash-section-progress-card dash-section-progress-card--m">
                <div class="mla-question-box mla-mock-box dash-overview-progress-box">
                  <div class="section-card-body">
                    <h4>
                      <img src="/images/brain.png" alt="" />
                      Mocks
                    </h4>
                    <HomepageProgress
                      :progress="isOverviewScoreMode ? mockProgress.scorePercent : mockProgress.percent"
                      :label="isOverviewScoreMode ? 'Score' : 'Progress'"
                      :mode="isOverviewScoreMode ? 'score' : 'progress'"
                      :detail="isOverviewScoreMode
                        ? `${mockProgress.correct}/${mockProgress.total}`
                        : `${mockProgress.attempted}/${mockProgress.total}`"
                    />
                  </div>
                  <div class="cardbottom-shadow">
                    <img src="/images/cardshadow.png" alt="" />
                  </div>
                </div>
                <div
                  class="dash-section-progress-body"
                  role="button"
                  tabindex="0"
                  title="Click to expand chart"
                  @click="openChartExpand('mocks')"
                  @keydown.enter.prevent="openChartExpand('mocks')"
                  @keydown.space.prevent="openChartExpand('mocks')"
                >
                  <ScoreTrendChart
                    v-if="isOverviewScoreMode"
                    :key="'m-score-' + chartAnimKey"
                    title="Mocks score"
                    :rows="mocksScoreRows"
                    bar-color="#ed1c24"
                    :passing-score="scorePassingPercent"
                    :average-score="scoreAveragePercent"
                  />
                  <ProgressLineChart
                    v-else
                    :key="'m-line-' + chartAnimKey"
                    title="Mocks progress"
                    :weeks="progressTrendWeeks"
                    :series="mocksLineSeries"
                    tint="rgba(237, 28, 36, 0.10)"
                    :show-legend="false"
                  />
                  <button
                    type="button"
                    class="dash-section-progress-refresh"
                    title="Reset Mocks scoring"
                    aria-label="Reset Mocks scoring"
                    @click.stop="openSectionResetConfirm('mocks')"
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                      <path fill="currentColor" d="M12 6V3L8 7l4 4V8c2.76 0 5 2.24 5 5a5 5 0 0 1-8.66 3.44l-1.42 1.42A7 7 0 0 0 19 13c0-3.87-3.13-7-7-7zm0 12v3l4-4-4-4v3a5 5 0 0 1-5-5c0-.95.27-1.83.74-2.59l-1.48-1.3A6.97 6.97 0 0 0 5 13c0 3.87 3.13 7 7 7z"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <div v-if="!isOverviewScoreMode" class="dash-combined-card">
              <div
                class="dash-chart-block dash-chart-block--clickable dash-chart-block--combined"
                role="button"
                tabindex="0"
                title="Click to expand chart"
                @click="openChartExpand('combined')"
                @keydown.enter.prevent="openChartExpand('combined')"
                @keydown.space.prevent="openChartExpand('combined')"
              >
                <div class="dash-panel-head">
                  <h3>All progress — by week</h3>
                  <p>Questions, notes and mocks together · Click to expand</p>
                </div>
                <ProgressLineChart
                  :key="'c-line-' + chartAnimKey"
                  title=""
                  :weeks="progressTrendWeeks"
                  :series="combinedLineSeries"
                />
              </div>
            </div>

            <!-- <div class="dash-info-row">
              <div class="dash-panel dash-profile-panel">
                <h3>Personal information</h3>
                <ul class="dash-info-list">
                  <li><strong>Full name</strong><span>{{ profile.fullname || '—' }}</span></li>
                  <li><strong>Username</strong><span>{{ profile.username || '—' }}</span></li>
                  <li><strong>Email</strong><span>{{ profile.email || '—' }}</span></li>
                  <li><strong>Member since</strong><span>{{ profile.member_since || '—' }}</span></li>
                  <li><strong>Email verified</strong><span>{{ profile.email_verified ? 'Yes' : 'No' }}</span></li>
                </ul>
              </div>
              <div class="dash-panel dash-sub-panel">
                <h3>Subscription</h3>
                <div class="dash-sub-card">
                  <span class="dash-sub-plan">{{ subscription.plan }}</span>
                  <span class="dash-sub-status" :class="{ active: subscription.status === 'Active' }">
                    {{ subscription.status }}
                  </span>
                  <p class="dash-sub-since">Member since {{ subscription.member_since || '—' }}</p>
                </div>
                <div class="dash-mini-stats">
                  <div><strong>{{ flaggedQuestions.length }}</strong><span>Flagged questions</span></div>
                  <div><strong>{{ flaggedMocks.length }}</strong><span>Flagged mocks</span></div>
                  <div><strong>{{ flaggedNotes.length }}</strong><span>Flagged notes</span></div>
                  <div><strong>{{ comments.length }}</strong><span>Comments</span></div>
                  <div><strong>{{ journalsTotalCount }}</strong><span>Journals</span></div>
                </div>
              </div>
            </div> -->
          </section>

          <!-- Flags -->
          <section v-show="activeSection === 'flags'" class="dash-section">
            <div class="dash-list-grid">
              <div class="dash-panel dash-panel--accent dash-panel--q">
                <h3>Flagged questions <span class="count">({{ flaggedQuestions.length }})</span></h3>
                <ul v-if="flaggedQuestions.length" class="dash-list">
                  <li v-for="item in flaggedQuestions" :key="'fq-' + item.id">
                    <button
                      type="button"
                      class="dash-list-code dash-list-code--btn"
                      @click="openFlagPreview('question', item)"
                    >{{ item.code || '—' }}</button>
                    <span class="dash-list-title">
                      {{ item.condition || 'Question' }}
                      <template v-if="item.subcondition"> · {{ item.subcondition }}</template>
                    </span>
                  </li>
                </ul>
                <p v-else class="dash-empty">No flagged questions yet.</p>
              </div>
              <div class="dash-panel dash-panel--accent dash-panel--n">
                <h3>Flagged notes <span class="count">({{ flaggedNotes.length }})</span></h3>
                <ul v-if="flaggedNotes.length" class="dash-list">
                  <li v-for="item in flaggedNotes" :key="'fn-' + item.id">
                    <button
                      type="button"
                      class="dash-list-code dash-list-code--btn"
                      @click="openFlagPreview('note', item)"
                    >{{ item.number || '—' }}</button>
                    <span class="dash-list-title">{{ item.title || 'Note' }}</span>
                  </li>
                </ul>
                <p v-else class="dash-empty">No flagged notes yet.</p>
              </div>
              <div class="dash-panel dash-panel--accent dash-panel--m">
                <h3>Flagged mock questions <span class="count">({{ flaggedMocks.length }})</span></h3>
                <ul v-if="flaggedMocks.length" class="dash-list">
                  <li v-for="item in flaggedMocks" :key="'fm-' + item.id">
                    <button
                      type="button"
                      class="dash-list-code dash-list-code--btn"
                      @click="openFlagPreview('mock', item)"
                    >{{ mockFlagCode(item) }}</button>
                    <span class="dash-list-title">{{ item.mock_title || 'Mock' }}</span>
                  </li>
                </ul>
                <p v-else class="dash-empty">No flagged mock questions yet.</p>
              </div>
            </div>
          </section>

          <!-- Comments -->
          <section v-show="activeSection === 'comments'" class="dash-section">
            <div class="dash-list-grid">
              <div class="dash-panel dash-panel--accent dash-panel--q">
                <h3>Question comments <span class="count">({{ questionComments.length }})</span></h3>
                <ul v-if="questionComments.length" class="dash-list">
                  <li v-for="item in questionComments" :key="'cq-' + item.id">
                    <button
                      type="button"
                      class="dash-list-code dash-list-code--btn"
                      @click="openFlagPreview('question', item)"
                    >{{ item.code || '—' }}</button>
                    <span class="dash-list-title">
                      <template v-if="item.condition || item.subcondition">
                        {{ item.condition || 'Question' }}
                        <template v-if="item.subcondition"> · {{ item.subcondition }}</template>
                      </template>
                      <template v-else>{{ item.text || 'Comment' }}</template>
                    </span>
                    <p v-if="item.condition || item.subcondition" class="dash-list-snippet">{{ item.text }}</p>
                  </li>
                </ul>
                <p v-else class="dash-empty">No question comments yet.</p>
              </div>
              <div class="dash-panel dash-panel--accent dash-panel--n">
                <h3>Note comments <span class="count">({{ noteComments.length }})</span></h3>
                <ul v-if="noteComments.length" class="dash-list">
                  <li v-for="item in noteComments" :key="'cn-' + item.id">
                    <button
                      type="button"
                      class="dash-list-code dash-list-code--btn"
                      @click="openFlagPreview('note', item)"
                    >{{ item.number || '—' }}</button>
                    <span class="dash-list-title">{{ item.title || 'Note' }}</span>
                    <p v-if="item.text || item.preview" class="dash-list-snippet">{{ item.text || item.preview }}</p>
                  </li>
                </ul>
                <p v-else class="dash-empty">No note comments yet.</p>
              </div>
              <div class="dash-panel dash-panel--accent dash-panel--m">
                <h3>Mock comments <span class="count">({{ mockComments.length }})</span></h3>
                <ul v-if="mockComments.length" class="dash-list">
                  <li v-for="item in mockComments" :key="'cm-' + item.id">
                    <button
                      type="button"
                      class="dash-list-code dash-list-code--btn"
                      @click="openFlagPreview('mock', item)"
                    >{{ mockFlagCode(item) }}</button>
                    <span class="dash-list-title">{{ item.mock_title || 'Mock' }}</span>
                    <p v-if="item.text" class="dash-list-snippet">{{ item.text }}</p>
                  </li>
                </ul>
                <p v-else class="dash-empty">No mock comments yet.</p>
              </div>
            </div>
          </section>

          <!-- Journals -->
          <section v-show="activeSection === 'journals'" class="dash-section">
            <div class="dash-list-grid">
              <div class="dash-panel dash-panel--accent dash-panel--q">
                <h3>Question journals <span class="count">({{ journalsQuestions.length }})</span></h3>
                <ul v-if="journalsQuestions.length" class="dash-list">
                  <li v-for="item in journalsQuestions" :key="'jq-' + item.id">
                    <button
                      type="button"
                      class="dash-list-code dash-list-code--btn"
                      @click="openFlagPreview('question', item)"
                    >{{ item.code || '—' }}</button>
                    <span class="dash-list-title">
                      <template v-if="item.condition || item.subcondition">
                        {{ item.condition || 'Question' }}
                        <template v-if="item.subcondition"> · {{ item.subcondition }}</template>
                      </template>
                      <template v-else>Question</template>
                    </span>
                    <p v-if="item.preview" class="dash-list-snippet">{{ item.preview }}</p>
                  </li>
                </ul>
                <p v-else class="dash-empty">No question journals yet.</p>
              </div>
              <div class="dash-panel dash-panel--accent dash-panel--n">
                <h3>Note journals <span class="count">({{ journals.length }})</span></h3>
                <ul v-if="journals.length" class="dash-list">
                  <li v-for="item in journals" :key="'jn-' + item.id">
                    <button
                      type="button"
                      class="dash-list-code dash-list-code--btn"
                      @click="openFlagPreview('note', item)"
                    >{{ item.number || '—' }}</button>
                    <span class="dash-list-title">{{ item.title || 'Untitled' }}</span>
                    <p v-if="item.preview" class="dash-list-snippet">{{ item.preview }}</p>
                  </li>
                </ul>
                <p v-else class="dash-empty">No note journals yet.</p>
              </div>
              <div class="dash-panel dash-panel--accent dash-panel--m">
                <h3>Mock journals <span class="count">({{ journalsMocks.length }})</span></h3>
                <ul v-if="journalsMocks.length" class="dash-list">
                  <li v-for="item in journalsMocks" :key="'jm-' + item.id">
                    <button
                      type="button"
                      class="dash-list-code dash-list-code--btn"
                      @click="openFlagPreview('mock', item)"
                    >{{ mockFlagCode(item) }}</button>
                    <span class="dash-list-title">{{ item.mock_title || 'Mock' }}</span>
                    <p v-if="item.preview" class="dash-list-snippet">{{ item.preview }}</p>
                  </li>
                </ul>
                <p v-else class="dash-empty">No mock journals yet.</p>
              </div>
            </div>
          </section>

          <!-- Feedback -->
          <section v-show="activeSection === 'feedback'" class="dash-section">
            <div class="dash-list-grid">
              <div class="dash-panel dash-panel--accent dash-panel--q">
                <h3>Question feedback <span class="count">({{ feedbackQuestions.length }})</span></h3>
                <ul v-if="feedbackQuestions.length" class="dash-list">
                  <li v-for="item in feedbackQuestions" :key="'fqfb-' + item.id">
                    <button
                      type="button"
                      class="dash-list-code dash-list-code--btn"
                      @click="openFlagPreview('question', item)"
                    >{{ item.code || '—' }}</button>
                    <span class="dash-list-title">
                      {{ item.condition || 'Question' }}
                      <template v-if="item.subcondition"> · {{ item.subcondition }}</template>
                    </span>
                    <p v-if="item.preview" class="dash-list-snippet">{{ item.preview }}</p>
                  </li>
                </ul>
                <p v-else class="dash-empty">No question feedback yet.</p>
              </div>

              <div class="dash-panel dash-panel--accent dash-panel--n">
                <h3>Note feedback <span class="count">({{ feedbackNotes.length }})</span></h3>
                <ul v-if="feedbackNotes.length" class="dash-list">
                  <li v-for="item in feedbackNotes" :key="'fnfb-' + item.id">
                    <button
                      type="button"
                      class="dash-list-code dash-list-code--btn"
                      @click="openFlagPreview('note', item)"
                    >{{ item.number || '—' }}</button>
                    <span class="dash-list-title">{{ item.title || 'Untitled' }}</span>
                    <p v-if="item.preview" class="dash-list-snippet">{{ item.preview }}</p>
                  </li>
                </ul>
                <p v-else class="dash-empty">No note feedback yet.</p>
              </div>

              <div class="dash-panel dash-panel--accent dash-panel--m">
                <h3>Mock feedback <span class="count">({{ feedbackMocks.length }})</span></h3>
                <ul v-if="feedbackMocks.length" class="dash-list">
                  <li v-for="item in feedbackMocks" :key="'fmfb-' + item.id">
                    <button
                      type="button"
                      class="dash-list-code dash-list-code--btn"
                      @click="openFlagPreview('mock', item)"
                    >{{ mockFlagCode(item) }}</button>
                    <span class="dash-list-title">{{ item.mock_title || 'Mock' }}</span>
                    <p v-if="item.preview" class="dash-list-snippet">{{ item.preview }}</p>
                  </li>
                </ul>
                <p v-else class="dash-empty">No mock feedback yet.</p>
              </div>
            </div>
          </section>

          <!-- Settings -->
          <section v-show="activeSection === 'settings'" class="dash-section">
            <div class="dash-info-row">
              <div class="dash-panel dash-profile-panel">
                <h3>Account</h3>
                <ul class="dash-info-list">
                  <li class="dash-account-row">
                    <strong>Full name</strong>
                    <span class="dash-account-value">
                      <span>{{ profile.fullname || '—' }}</span>
                      <button
                        type="button"
                        class="dash-account-edit"
                        title="Edit full name"
                        aria-label="Edit full name"
                        @click="openAccountEdit('fullname')"
                      >
                        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                          <path
                            fill="currentColor"
                            d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z"
                          />
                        </svg>
                      </button>
                    </span>
                  </li>
                  <li class="dash-account-row">
                    <strong>Username</strong>
                    <span class="dash-account-value">
                      <span>{{ profile.username || '—' }}</span>
                      <button
                        type="button"
                        class="dash-account-edit"
                        title="Edit username"
                        aria-label="Edit username"
                        @click="openAccountEdit('username')"
                      >
                        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                          <path
                            fill="currentColor"
                            d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z"
                          />
                        </svg>
                      </button>
                    </span>
                  </li>
                  <li class="dash-account-row">
                    <strong>Password</strong>
                    <span class="dash-account-value">
                      <span>••••••••</span>
                      <button
                        type="button"
                        class="dash-account-edit"
                        title="Change password"
                        aria-label="Change password"
                        @click="openAccountEdit('password')"
                      >
                        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                          <path
                            fill="currentColor"
                            d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z"
                          />
                        </svg>
                      </button>
                    </span>
                  </li>
                  <li><strong>Email</strong><span>{{ profile.email || '—' }}</span></li>
                  <li><strong>Member since</strong><span>{{ profile.member_since || '—' }}</span></li>
                  <li class="dash-delete-account-row">
                    <button type="button" class="dash-delete-account-link" @click="openDeleteAccountConfirm">
                      Delete account/profile
                    </button>
                  </li>
                </ul>
              </div>

              <div class="dash-panel dash-sub-panel">
                <div class="dash-settings-cards questions-notes-mocks">
                  <div
                    v-for="card in settingsSectionCards"
                    :key="'settings-' + card.key"
                    class="dash-sub-row"
                  >
                    <div
                      class="mla-question-box"
                      :class="{
                        'mla-note-box': card.key === 'notes',
                        'mla-mock-box': card.key === 'mocks',
                        'is-section-locked': isSectionLocked(card.key),
                      }"
                      :style="{ cursor: 'pointer' }"
                      :title="isSectionLocked(card.key) ? 'Locked Section' : card.title"
                      @click="goSection(card.key, card.route)"
                    >
                      <SectionLockBadge v-if="isSectionLocked(card.key)" />
                      <div class="section-card-body">
                        <h4>
                          <img :src="card.image" :alt="card.title" />
                          {{ card.title }}
                        </h4>
                        <!-- <HomepageProgress
                          :progress="settingsProgressFor(card.key)"
                          label="Progress"
                          :detail="settingsProgressDetailFor(card.key)"
                        /> -->
                      </div>
                      <div class="cardbottom-shadow">
                        <img src="/images/cardshadow.png" alt="" />
                      </div>
                    </div>

                    <div class="dash-sub-section-status">
                      <div
                        class="dash-subscribed-wrap"
                        :class="{ 'is-not-subscribed': isSectionLocked(card.key) }"
                      >
                        <span class="dash-subscribed-text">
                          <template v-if="isSectionLocked(card.key)">
                            Not SUBSCRIBED
                          </template>
                          <template v-else>
                            SUBSCRIBED
                            <small>Since {{ subscription.member_since || profile.member_since || '—' }}</small>
                          </template>
                        </span>
                        <span
                          v-if="!isSectionLocked(card.key)"
                          class="dash-subscribed-check"
                          aria-hidden="true"
                        >
                          <svg viewBox="0 0 24 24" width="22" height="22">
                            <circle cx="12" cy="12" r="11" fill="#9ded6c" />
                            <path
                              d="M7.5 12.5 L10.5 15.5 L16.5 9"
                              fill="none"
                              stroke="#1b5e20"
                              stroke-width="2.2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                        </span>
                      </div>
                    </div>

                    <div class="dash-sub-section-action">
                      <button
                        v-if="isSectionLocked(card.key)"
                        type="button"
                        class="dash-subscribe-btn"
                        :disabled="sectionResetSubmitting && sectionResetKind === card.key"
                        @click.stop="openSubscribeConfirm(card.key)"
                      >
                        SUBSCRIBE
                      </button>
                      <button
                        v-else
                        type="button"
                        class="dash-subscribe-btn dash-unsubscribe-btn"
                        :disabled="sectionResetSubmitting && sectionResetKind === card.key"
                        @click.stop="openUnsubscribeConfirm(card.key)"
                      >
                        UNSUBSCRIBE
                      </button>
                    </div>
                  </div>
                </div>

                <!-- <div class="dash-sub-card dash-sub-card--plan">
                  <span class="dash-sub-plan">{{ subscription.plan }}</span>
                  <span class="dash-sub-status" :class="{ active: subscription.status === 'Active' }">
                    {{ subscription.status }}
                  </span>
                  <p class="dash-sub-since">Member since {{ subscription.member_since || '—' }}</p>
                </div> -->

                <!-- <button type="button" class="dash-settings-logout" @click="logout">Logout</button> -->
              </div>
            </div>
          </section>
        </template>
      </main>
    </div>

    <!-- Chart expand modal -->
    <transition name="dash-preview-fade">
      <div
        v-if="chartExpandOpen"
        class="dash-preview-overlay"
        @click.self="closeChartExpand"
      >
        <div
          class="dash-chart-expand-modal"
          role="dialog"
          aria-modal="true"
          :aria-label="chartExpandTitle"
        >
          <div class="dash-preview-header" :style="{ borderColor: chartExpandColor }">
            <div>
              <p class="dash-preview-type">{{ chartExpandTypeLabel }}</p>
              <h3>{{ chartExpandTitle }}</h3>
            </div>
            <button type="button" class="dash-preview-close" aria-label="Close" @click="closeChartExpand">
              <svg width="14" height="14" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.38013 0.75L8.1701 7.54001" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round" />
                <path d="M8.1701 0.75L1.38013 7.54001" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round" />
              </svg>
            </button>
          </div>
          <div class="dash-chart-expand-body">
            <ProgressLineChart
              v-if="chartExpandKind === 'notes'"
              :key="'n-expand-' + chartAnimKey + '-' + chartExpandOpen"
              title="Notes progress"
              :weeks="progressTrendWeeks"
              :series="notesLineSeries"
              tint="rgba(245, 156, 0, 0.12)"
              :show-legend="false"
              expanded
            />
            <ProgressLineChart
              v-else-if="chartExpandKind === 'combined'"
              :key="'c-expand-' + chartAnimKey"
              title="Overall progress - Questions, Notes & Mocks"
              :weeks="progressTrendWeeks"
              :series="combinedLineSeries"
              expanded
            />
            <ScoreTrendChart
              v-else-if="chartExpandKind === 'mocks' && isOverviewScoreMode"
              :key="'m-score-expand-' + chartAnimKey"
              title="Mocks score"
              :rows="mocksScoreRows"
              bar-color="#ed1c24"
              :passing-score="scorePassingPercent"
              :average-score="scoreAveragePercent"
              expanded
            />
            <ProgressLineChart
              v-else-if="chartExpandKind === 'mocks'"
              :key="'m-expand-' + chartAnimKey"
              title="Mocks progress"
              :weeks="progressTrendWeeks"
              :series="mocksLineSeries"
              tint="rgba(237, 28, 36, 0.10)"
              :show-legend="false"
              expanded
            />
            <ScoreTrendChart
              v-else-if="isOverviewScoreMode"
              :key="'q-score-expand-' + chartAnimKey"
              title="Questions score"
              :rows="questionsScoreRows"
              bar-color="#1a90ff"
              :passing-score="scorePassingPercent"
              :average-score="scoreAveragePercent"
              expanded
            />
            <ProgressLineChart
              v-else
              :key="'q-expand-' + chartAnimKey"
              title="Questions progress"
              :weeks="progressTrendWeeks"
              :series="questionsLineSeries"
              tint="rgba(26, 144, 255, 0.10)"
              :show-legend="false"
              expanded
            />
          </div>
        </div>
      </div>
    </transition>

    <!-- Flagged item preview — portal-style at page root (same pattern as linked Q/N/M) -->
    <transition name="dash-preview-fade">
      <div
        v-if="previewOpen"
        class="dash-preview-overlay"
        @click.self="closeFlagPreview"
      >
        <div class="dash-preview-modal" role="dialog" aria-modal="true" :aria-label="previewTitle">
          <div class="dash-preview-header" :style="{ borderColor: previewColor }">
            <div>
              <p class="dash-preview-type">{{ previewTypeLabel }}</p>
              <h3>{{ previewTitle }}</h3>
            </div>
            <button type="button" class="dash-preview-close" aria-label="Close" @click="closeFlagPreview">
              <svg width="14" height="14" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.38013 0.75L8.1701 7.54001" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round" />
                <path d="M8.1701 0.75L1.38013 7.54001" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round" />
              </svg>
            </button>
          </div>
          <div class="dash-preview-frame-wrap">
            <div v-if="previewError" class="dash-preview-error">{{ previewError }}</div>
            <iframe
              v-else-if="previewSrc"
              :src="previewSrc"
              class="dash-preview-frame"
              :title="previewTitle"
            />
          </div>
          <!-- <div class="dash-preview-footer">
            <button
              type="button"
              class="dash-preview-goto"
              :style="{ backgroundColor: previewColor, borderColor: previewColor }"
              :disabled="!canGoToPreviewItem"
              @click="goToPreviewItem"
            >{{ previewGoLabel }}</button>
          </div> -->
        </div>
      </div>
    </transition>

    <SectionSubscribeModal
      :open="showSubscribePopup"
      :message="subscribeMessage"
      @close="showSubscribePopup = false"
    />

    <transition name="dash-preview-fade">
      <div
        v-if="examReminderOpen"
        class="dash-exam-reminder-overlay"
        @click.self="dismissExamReminder"
      >
        <div
          class="dash-exam-reminder-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Exam date reminder"
        >
          <button
            type="button"
            class="dash-exam-reminder-close"
            aria-label="Close"
            @click="dismissExamReminder"
          >
            <svg width="14" height="14" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1.38013 0.75L8.1701 7.54001" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round" />
              <path d="M8.1701 0.75L1.38013 7.54001" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round" />
            </svg>
          </button>
          <p class="dash-exam-reminder-kicker">Exam reminder</p>
          <p class="dash-exam-reminder-date">{{ examBannerLabel }}</p>
          <p class="dash-exam-reminder-days">
            <template v-if="examDaysRemaining === 0">Your exam is today</template>
            <template v-else-if="examDaysRemaining === 1">1 day remaining</template>
            <template v-else>{{ examDaysRemaining }} days remaining</template>
          </p>
          <button type="button" class="dash-exam-reminder-ok" @click="dismissExamReminder">
            Got it
          </button>
        </div>
      </div>
    </transition>

    <transition name="dash-preview-fade">
      <div
        v-if="sectionResetOpen"
        class="dash-preview-overlay"
        @click.self="closeSectionResetConfirm"
      >
        <div
          class="dash-section-reset-modal"
          role="dialog"
          aria-modal="true"
          :aria-label="sectionResetTitle"
        >
          <div class="dash-section-reset-header" :style="{ borderColor: sectionResetColor }">
            <h3>{{ sectionResetTitle }}</h3>
            <button
              type="button"
              class="dash-preview-close"
              aria-label="Close"
              @click="closeSectionResetConfirm"
            >
              <svg width="14" height="14" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.38013 0.75L8.1701 7.54001" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round" />
                <path d="M8.1701 0.75L1.38013 7.54001" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round" />
              </svg>
            </button>
          </div>
          <div class="dash-section-reset-body">
            <p class="dash-section-reset-message">{{ sectionResetMessage }}</p>
            <label class="dash-section-reset-label" for="dash-section-reset-password">
              Enter your password to confirm
            </label>
            <input
              id="dash-section-reset-password"
              ref="sectionResetPasswordInput"
              v-model="sectionResetPassword"
              type="password"
              class="dash-section-reset-input"
              autocomplete="current-password"
              :disabled="sectionResetSubmitting"
              @keydown.enter.prevent="confirmSectionReset"
            />
            <p v-if="sectionResetError" class="dash-section-reset-error">{{ sectionResetError }}</p>
            <div class="dash-section-reset-actions">
              <button
                type="button"
                class="dash-section-reset-cancel"
                :disabled="sectionResetSubmitting"
                @click="closeSectionResetConfirm"
              >
                Cancel
              </button>
              <button
                type="button"
                class="dash-section-reset-confirm"
                :disabled="sectionResetSubmitting || !sectionResetPassword"
                @click="confirmSectionReset"
              >
                {{ sectionResetConfirmLabel }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Account field edit modal -->
    <transition name="dash-preview-fade">
      <div
        v-if="accountEditOpen"
        class="dash-preview-overlay dash-section-reset-overlay"
        @click.self="closeAccountEdit"
      >
        <div
          class="dash-section-reset-modal"
          role="dialog"
          aria-modal="true"
          :aria-label="accountEditTitle"
        >
          <div class="dash-section-reset-header" style="border-color: #1a90ff">
            <h3>{{ accountEditTitle }}</h3>
            <button
              type="button"
              class="dash-preview-close"
              aria-label="Close"
              @click="closeAccountEdit"
            >
              <svg width="14" height="14" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.38013 0.75L8.1701 7.54001" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round" />
                <path d="M8.1701 0.75L1.38013 7.54001" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round" />
              </svg>
            </button>
          </div>
          <div class="dash-section-reset-body">
            <template v-if="accountEditField === 'password'">
              <label class="dash-section-reset-label" for="dash-account-current-password">
                Current password
              </label>
              <input
                id="dash-account-current-password"
                ref="accountEditFirstInput"
                v-model="accountEditCurrentPassword"
                type="password"
                class="dash-section-reset-input"
                autocomplete="current-password"
                :disabled="accountEditSubmitting"
              />
              <label class="dash-section-reset-label" for="dash-account-new-password">
                New password
              </label>
              <input
                id="dash-account-new-password"
                v-model="accountEditPassword"
                type="password"
                class="dash-section-reset-input"
                autocomplete="new-password"
                :disabled="accountEditSubmitting"
              />
              <label class="dash-section-reset-label" for="dash-account-confirm-password">
                Confirm new password
              </label>
              <input
                id="dash-account-confirm-password"
                v-model="accountEditPasswordConfirm"
                type="password"
                class="dash-section-reset-input"
                autocomplete="new-password"
                :disabled="accountEditSubmitting"
                @keydown.enter.prevent="confirmAccountEdit"
              />
            </template>
            <template v-else>
              <label class="dash-section-reset-label" :for="'dash-account-value-' + accountEditField">
                {{ accountEditField === 'username' ? 'Username' : 'Full name' }}
              </label>
              <input
                :id="'dash-account-value-' + accountEditField"
                ref="accountEditFirstInput"
                v-model="accountEditValue"
                type="text"
                class="dash-section-reset-input"
                :autocomplete="accountEditField === 'username' ? 'username' : 'name'"
                :disabled="accountEditSubmitting"
              />
              <label class="dash-section-reset-label" for="dash-account-confirm-current">
                Enter your password to confirm
              </label>
              <input
                id="dash-account-confirm-current"
                v-model="accountEditCurrentPassword"
                type="password"
                class="dash-section-reset-input"
                autocomplete="current-password"
                :disabled="accountEditSubmitting"
                @keydown.enter.prevent="confirmAccountEdit"
              />
            </template>
            <p v-if="accountEditError" class="dash-section-reset-error">{{ accountEditError }}</p>
            <div class="dash-section-reset-actions">
              <button
                type="button"
                class="dash-section-reset-cancel"
                :disabled="accountEditSubmitting"
                @click="closeAccountEdit"
              >
                Cancel
              </button>
              <button
                type="button"
                class="dash-section-reset-confirm"
                :disabled="accountEditSubmitting || !accountEditCanSubmit"
                @click="confirmAccountEdit"
              >
                {{ accountEditSubmitting ? 'Saving…' : 'Save' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { get, byMethod, assetUrl } from './lib/api';
import ProgressLineChart from '../components/ProgressLineChart.vue';
import ScoreTrendChart from '../components/ScoreTrendChart.vue';
import HomepageProgress from '../components/HomepageProgress.vue';
import SectionLockBadge from '../components/SectionLockBadge.vue';
import SectionSubscribeModal from '../components/SectionSubscribeModal.vue';
import {
  hydrateNoteUserState,
  notesProgressStats,
  rebuildNotesNumberRegistry,
  invalidateNotesPrefsCache,
  clearAllNotesReadProgress,
} from './lib/notesUserPrefs';
import {
  mocksAggregateProgressPercent,
  mocksAggregateScorePercent,
  mocksAggregateTotals,
} from './lib/mocksAggregate';
import {
  emptySectionLocks,
  clearSectionLocksCache,
  setSectionLocksCache,
  subscribeSectionMessage,
} from './lib/sectionLocks';
import { lockBodyScrollInline, releaseBodyScrollLock } from './lib/bodyScrollLock';
import { onEmbeddedPreviewCloseRequest } from './lib/embeddedPreview';

const EMPTY_STATS = {
  questions: { total: 0, attempted: 0, correct: 0, incorrect: 0, percent: 0, score_percent: 0 },
  mocks: { total: 0, attempted: 0, correct: 0, incorrect: 0, percent: 0, score_percent: 0 },
  notes: { total: 0 },
};

export default {
  name: 'UserDashboard',
  components: {
    ProgressLineChart,
    ScoreTrendChart,
    HomepageProgress,
    SectionLockBadge,
    SectionSubscribeModal,
  },
  data() {
    return {
      loading: false,
      loaded: false,
      activeSection: 'overview',
      /** Overview cards: 'progress' | 'score' */
      overviewMetricMode: 'progress',
      profile: {},
      subscription: { plan: 'Synapse MLA Access', status: 'Active', member_since: null },
      stats: { ...EMPTY_STATS },
      flaggedQuestions: [],
      flaggedMocks: [],
      flaggedNotes: [],
      comments: [],
      journals: [],
      journalsQuestions: [],
      journalsMocks: [],
      feedbackQuestions: [],
      feedbackMocks: [],
      feedbackNotes: [],
      clinicalAreas: [],
      professionalKnowledge: [],
      notesClinicalAreas: [],
      notesProfessionalKnowledge: [],
      mocksData: [],
      notesReadRev: 0,
      menuOpen: false,
      sectionLocks: emptySectionLocks(),
      showSubscribePopup: false,
      subscribeMessage: '',
      sectionResetOpen: false,
      sectionResetAction: 'reset',
      sectionResetKind: '',
      sectionResetPassword: '',
      sectionResetError: '',
      sectionResetSubmitting: false,
      accountEditOpen: false,
      accountEditField: '',
      accountEditValue: '',
      accountEditCurrentPassword: '',
      accountEditPassword: '',
      accountEditPasswordConfirm: '',
      accountEditError: '',
      accountEditSubmitting: false,
      profileUploading: false,
      profileUploadError: '',
      profileAvatarCacheBust: 0,
      navItems: [
        {
          key: 'overview',
          label: 'Performance',
          icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 17 9 11 13 15 21 7"/><polyline points="15 7 21 7 21 13"/></svg>',
        },
        {
          key: 'flags',
          label: 'Flags',
          icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M5 3v18h2V14h9.6l-.8-3.2L18.4 6H7V3H5z" opacity=".95"/><path d="M7 6h10.2l-1.6 4.2 1.5 3.8H7V6z" fill="#ed1c24"/></svg>',
        },
         {
          key: 'feedback',
          label: 'Feedback',
          icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>',
        },
        {
          key: 'journals',
          label: 'Journals',
          icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 4h10a2 2 0 0 1 2 2v14l-3-2-3 2-3-2-3 2V6a2 2 0 0 1 2-2z"/><path d="M9 8h6M9 12h6"/></svg>',
        },
        {
          key: 'comments',
          label: 'Comments',
          icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a3 3 0 0 1-3 3H8l-4 3V6a3 3 0 0 1 3-3h11a3 3 0 0 1 3 3z"/></svg>',
        },
        
       
        {
          key: 'settings',
          label: 'Settings',
          icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9c.3.6.9 1 1.6 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"/></svg>',
        },
      ],
      settingsSectionCards: [
        {
          key: 'questions',
          title: 'Questions',
          image: '/images/questionmark.png',
          route: '/mlalistselection',
        },
        {
          key: 'notes',
          title: 'Notes',
          image: '/images/file.png',
          route: '/noteslistselection',
        },
        {
          key: 'mocks',
          title: 'Mocks',
          image: '/images/brain.png',
          route: '/mocksection',
        },
      ],
      examDateIso: null,
      examPanelOpen: false,
      examNow: Date.now(),
      examTickTimer: null,
      examReminderOpen: false,
      chartAnimKey: 0,
      chartExpandOpen: false,
      chartExpandKind: '',
      previewOpen: false,
      previewKind: '',
      previewItem: null,
      previewSrc: '',
      previewError: '',
      // Real chart series from /client-dashboard (empty until user has attempts)
      progressTrendWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
      combinedQuestionsProgress: [],
      combinedNotesProgress: [],
      combinedMocksProgress: [],
      questionsScoreTrend: [],
      scorePassingPercent: 70,
      scoreAveragePercent: 60,
    };
  },
  computed: {
    ...mapGetters(['user']),
    displayName() {
      return (this.profile.fullname || (this.user && this.user.fullname) || (this.user && this.user.username) || 'User').trim();
    },
    userInitials() {
      const name = this.displayName;
      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return name.slice(0, 2).toUpperCase();
    },
    profileFilename() {
      return (this.profile && this.profile.profile)
        || (this.user && this.user.profile)
        || '';
    },
    profileAvatarUrl() {
      const name = this.profileFilename;
      if (!name) return '';
      const url = assetUrl(`profile/${name}`);
      if (!url) return '';
      const bust = this.profileAvatarCacheBust;
      return bust ? `${url}${url.includes('?') ? '&' : '?'}v=${bust}` : url;
    },
    questionProgress() {
      const s = this.stats.questions || {};
      const total = this.questionsTotalFromAreas || s.total || 0;
      const attempted = this.questionsAttemptedFromAreas || s.attempted || 0;
      const percent = total > 0 ? Math.round((attempted / total) * 100) : (s.percent || 0);
      return {
        total,
        attempted,
        correct: s.correct || 0,
        incorrect: s.incorrect || 0,
        percent,
        scorePercent: s.score_percent || 0,
      };
    },
    questionsAttemptedFromAreas() {
      const all = [...this.clinicalAreas, ...this.professionalKnowledge];
      return all.reduce((sum, i) => sum + (parseInt(i.attempted_count, 10) || 0), 0);
    },
    questionsTotalFromAreas() {
      const all = [...this.clinicalAreas, ...this.professionalKnowledge];
      return all.reduce((sum, i) => sum + (parseInt(i.questions_count, 10) || 0), 0);
    },
    notesProgress() {
      void this.notesReadRev;
      const fromApi = this.stats.notes || {};
      const local = notesProgressStats(this.$store, [
        ...this.notesClinicalAreas,
        ...this.notesProfessionalKnowledge,
      ]);
      const total = local.total || fromApi.total || 0;
      const read = local.read || 0;
      return {
        total,
        read,
        percent: local.percent || (total > 0 ? Math.round((read / total) * 100) : 0),
      };
    },
    /** Notes read history is local-only — show current % as week 1, or blank if none. */
    notesProgressTrendPoints() {
      const pct = Number(this.notesProgress.percent) || 0;
      if (pct <= 0) return [];
      return [{ week: 1, progress: pct }];
    },
    mockProgress() {
      const s = this.stats.mocks || {};
      const agg = mocksAggregateTotals(this.mocksData);
      const total = agg.totalQuestions || s.total || 0;
      const attempted = agg.totalAttempted || s.attempted || 0;
      const correct = agg.totalCorrect || s.correct || 0;
      return {
        total,
        attempted,
        correct,
        incorrect: s.incorrect || 0,
        percent: total > 0 ? Math.round((attempted / total) * 100) : mocksAggregateProgressPercent(this.mocksData),
        scorePercent: total > 0
          ? mocksAggregateScorePercent(this.mocksData)
          : (s.score_percent || 0),
      };
    },
    isOverviewScoreMode() {
      return this.overviewMetricMode === 'score';
    },
    activeSectionLabel() {
      const item = (this.navItems || []).find((n) => n.key === this.activeSection);
      const label = (item && item.label) || 'Performance';
      if (this.activeSection === 'overview') {
        return this.isOverviewScoreMode ? 'Performance: Score' : 'Performance: Progress';
      }
      return label;
    },
    sectionResetSectionName() {
      if (this.sectionResetKind === 'notes') return 'Notes';
      if (this.sectionResetKind === 'mocks') return 'Mocks';
      return 'Questions';
    },
    sectionResetTitle() {
      if (this.sectionResetAction === 'delete-account') return 'Delete account/profile';
      const name = this.sectionResetSectionName;
      if (this.sectionResetAction === 'subscribe') return `Subscribe to ${name}`;
      if (this.sectionResetAction === 'unsubscribe') return `Unsubscribe from ${name}`;
      if (this.sectionResetKind === 'notes') return 'Reset Notes progress';
      if (this.sectionResetKind === 'mocks') return 'Reset Mocks scoring';
      return 'Reset Questions scoring';
    },
    sectionResetColor() {
      if (this.sectionResetAction === 'delete-account') return '#ed1c24';
      if (this.sectionResetKind === 'notes') return '#f59c00';
      if (this.sectionResetKind === 'mocks') return '#ed1c24';
      return '#1a90ff';
    },
    sectionResetMessage() {
      if (this.sectionResetAction === 'delete-account') {
        return 'Enter your password to request account/profile deletion.';
      }
      const name = this.sectionResetSectionName;
      if (this.sectionResetAction === 'subscribe') {
        return `Enter your password to subscribe to ${name} and unlock this section.`;
      }
      if (this.sectionResetAction === 'unsubscribe') {
        return `Enter your password to unsubscribe from ${name}. This will lock the section until you subscribe again.`;
      }
      const what = this.sectionResetKind === 'notes' ? 'progress' : 'scoring';
      return `Are you sure you want to reset your ${what} for ${name}?`;
    },
    sectionResetConfirmLabel() {
      if (this.sectionResetSubmitting) {
        if (this.sectionResetAction === 'subscribe') return 'Subscribing…';
        if (this.sectionResetAction === 'unsubscribe') return 'Unsubscribing…';
        if (this.sectionResetAction === 'delete-account') return 'Submitting…';
        return 'Deleting…';
      }
      if (this.sectionResetAction === 'subscribe') return 'Confirm subscribe';
      if (this.sectionResetAction === 'unsubscribe') return 'Confirm unsubscribe';
      if (this.sectionResetAction === 'delete-account') return 'Confirm';
      return 'Confirm delete';
    },
    accountEditTitle() {
      if (this.accountEditField === 'username') return 'Edit username';
      if (this.accountEditField === 'password') return 'Change password';
      return 'Edit full name';
    },
    accountEditCanSubmit() {
      const current = String(this.accountEditCurrentPassword || '').trim();
      if (!current) return false;
      if (this.accountEditField === 'password') {
        const next = String(this.accountEditPassword || '');
        const confirm = String(this.accountEditPasswordConfirm || '');
        return next.length >= 6 && next === confirm;
      }
      return String(this.accountEditValue || '').trim().length > 0;
    },
    questionsScoreRows() {
      return (this.questionsScoreTrend || []).map((pt) => ({
        key: `q-w-${pt.week}`,
        label: `Week ${pt.week}`,
        score: pt.score,
      }));
    },
    /** Up to 5 bars — one per attempted mock; grows as more mocks are completed. */
    mocksScoreRows() {
      const MAX = 5;
      const rows = [];
      (this.mocksData || []).forEach((item, index) => {
        if (rows.length >= MAX) return;
        if (!item || item.exit) return;
        if (!item.score || !item.score.length) return;
        const questLen = item.quest && item.quest.length ? item.quest.length : 0;
        if (!questLen) return;
        const correct = item.score.filter((s) => Number(s.correct) === 1).length;
        const score = Math.round((correct * 100) / questLen);
        const name = item.title || item.name || item.mock_title || `Mock ${index + 1}`;
        rows.push({
          key: item.id != null ? String(item.id) : `mock-${index}`,
          label: `Week ${rows.length + 1}`,
          score,
          tip: name,
        });
      });
      return rows;
    },
    overallProgress() {
      const q = this.questionProgress.percent;
      const n = this.notesProgress.percent;
      const m = this.mockProgress.percent;
      return Math.round((q + n + m) / 3);
    },
    questionComments() {
      return (this.comments || []).filter((c) => c.type === 'question');
    },
    mockComments() {
      return (this.comments || []).filter((c) => c.type === 'mock');
    },
    noteComments() {
      return (this.comments || []).filter((c) => c.type === 'note');
    },
    journalsTotalCount() {
      return (this.journalsQuestions || []).length
        + (this.journals || []).length
        + (this.journalsMocks || []).length;
    },
    questionsLineSeries() {
      return [{
        key: 'questions',
        label: 'Questions',
        color: '#1a90ff',
        stroke: '#0d6ecd',
        points: this.combinedQuestionsProgress,
      }];
    },
    notesLineSeries() {
      return [{
        key: 'notes',
        label: 'Notes',
        color: '#f59c00',
        stroke: '#c47d00',
        points: this.notesProgressTrendPoints,
      }];
    },
    mocksLineSeries() {
      return [{
        key: 'mocks',
        label: 'Mocks',
        color: '#ed1c24',
        stroke: '#b31218',
        points: this.combinedMocksProgress,
      }];
    },
    combinedLineSeries() {
      return [
        ...this.questionsLineSeries,
        ...this.notesLineSeries,
        ...this.mocksLineSeries,
      ];
    },
    examBannerLabel() {
      if (!this.examDateIso) return 'Exam date — Set date';
      return `Exam date ${this.formatExamDisplayDate(this.examDateIso)}`;
    },
    examDaysRemaining() {
      void this.examNow;
      if (!this.examDateIso) return null;
      const parts = String(this.examDateIso).split('-').map(Number);
      if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
      const target = new Date(parts[0], parts[1] - 1, parts[2], 23, 59, 59, 999);
      const diff = target.getTime() - Date.now();
      if (diff <= 0) return null;
      return Math.floor(diff / 86400000);
    },
    isExamUrgent() {
      const days = this.examDaysRemaining;
      return days !== null && days < 7;
    },
    previewColor() {
      if (this.previewKind === 'note') return '#FBAD18';
      if (this.previewKind === 'mock') return '#ED1C24';
      return '#00A2FD';
    },
    previewTypeLabel() {
      if (this.previewKind === 'note') return 'Note';
      if (this.previewKind === 'mock') return 'Mock';
      return 'Question';
    },
    previewTitle() {
      const item = this.previewItem || {};
      if (this.previewKind === 'note') {
        return item.title || item.number || 'Note';
      }
      if (this.previewKind === 'mock') {
        return this.mockFlagCode(item) || item.mock_title || 'Mock';
      }
      return item.code || [item.condition, item.subcondition].filter(Boolean).join(' · ') || 'Question';
    },
    previewGoLabel() {
      if (this.previewKind === 'note') return 'Go to note';
      if (this.previewKind === 'mock') return 'Go to mock';
      return 'Go to question';
    },
    canGoToPreviewItem() {
      return Boolean(this.getFlagPreviewLocation(this.previewKind, this.previewItem));
    },
    chartExpandColor() {
      if (this.chartExpandKind === 'notes') return '#f59c00';
      if (this.chartExpandKind === 'mocks') return '#ed1c24';
      if (this.chartExpandKind === 'combined') return '#6d6e71';
      return '#1a90ff';
    },
    chartExpandTypeLabel() {
      if (this.chartExpandKind === 'notes') return 'Notes';
      if (this.chartExpandKind === 'mocks') return 'Mocks';
      if (this.chartExpandKind === 'combined') return 'All progress';
      return 'Questions';
    },
    chartExpandTitle() {
      const score = this.isOverviewScoreMode;
      if (this.chartExpandKind === 'notes') return 'Notes progress by week';
      if (this.chartExpandKind === 'mocks') {
        return score ? 'Mocks score by week' : 'Mocks progress by week';
      }
      if (this.chartExpandKind === 'combined') return 'All progress by week';
      return score ? 'Questions score by week' : 'Questions progress by week';
    },
    examCountdown() {
      void this.examNow;
      if (!this.examDateIso) {
        return { weeks: 0, days: 0, time: '0:00:00' };
      }
      const parts = String(this.examDateIso).split('-').map(Number);
      if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
        return { weeks: 0, days: 0, time: '0:00:00' };
      }
      const target = new Date(parts[0], parts[1] - 1, parts[2], 23, 59, 59, 999);
      const diff = target.getTime() - Date.now();
      if (diff <= 0) {
        return { weeks: 0, days: 0, time: '0:00:00' };
      }
      const dayMs = 86400000;
      const hourMs = 3600000;
      const minuteMs = 60000;
      const secondMs = 1000;
      const totalDays = Math.floor(diff / dayMs);
      const weeks = Math.floor(totalDays / 7);
      const hours = Math.floor(diff / hourMs);
      const minutes = Math.floor((diff % hourMs) / minuteMs);
      const seconds = Math.floor((diff % minuteMs) / secondMs);
      return {
        weeks,
        days: totalDays,
        time: `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
      };
    },
  },
  created() {
    hydrateNoteUserState();
    this.loadExamDate();
    this.loadDashboard();
    this.loadProgressSources();
    this.maybeShowExamReminder();
  },
  mounted() {
    this._notesPrefsStorage = (ev) => {
      if (ev && ev.key && String(ev.key).indexOf('notesUserPrefs_') === 0) {
        invalidateNotesPrefsCache();
        this.notesReadRev += 1;
      }
    };
    window.addEventListener('storage', this._notesPrefsStorage);
    this._menuOutsideHandler = (e) => {
      const menu = this.$refs.topMenu;
      if (this.menuOpen && menu && !menu.contains(e.target)) {
        this.menuOpen = false;
      }
    };
    document.addEventListener('click', this._menuOutsideHandler);
    this._flagEscHandler = (e) => {
      if (e.key !== 'Escape') return;
      if (this.examReminderOpen) {
        this.dismissExamReminder();
        return;
      }
      if (this.accountEditOpen) {
        this.closeAccountEdit();
        return;
      }
      if (this.sectionResetOpen) {
        this.closeSectionResetConfirm();
        return;
      }
      if (this.previewOpen) {
        this.closeFlagPreview();
        return;
      }
      if (this.chartExpandOpen) {
        this.closeChartExpand();
      }
    };
    document.addEventListener('keydown', this._flagEscHandler);
    this._embeddedPreviewCloseOff = onEmbeddedPreviewCloseRequest(() => {
      if (this.previewOpen) this.closeFlagPreview();
    });
    this.examTickTimer = window.setInterval(() => {
      this.examNow = Date.now();
    }, 1000);
  },
  beforeDestroy() {
    if (this._notesPrefsStorage) {
      window.removeEventListener('storage', this._notesPrefsStorage);
    }
    if (this._menuOutsideHandler) {
      document.removeEventListener('click', this._menuOutsideHandler);
    }
    if (this._flagEscHandler) {
      document.removeEventListener('keydown', this._flagEscHandler);
    }
    if (this._embeddedPreviewCloseOff) {
      this._embeddedPreviewCloseOff();
      this._embeddedPreviewCloseOff = null;
    }
    if (this.examTickTimer) {
      clearInterval(this.examTickTimer);
    }
    this.closeFlagPreview();
    this.closeChartExpand();
  },
  methods: {
    isSectionLocked(key) {
      return !!(this.sectionLocks && this.sectionLocks[key]);
    },
    settingsProgressFor(key) {
      if (key === 'questions') return this.questionProgress.percent;
      if (key === 'notes') return this.notesProgress.percent;
      if (key === 'mocks') return this.mockProgress.percent;
      return 0;
    },
    settingsProgressDetailFor(key) {
      if (key === 'questions') {
        return `${this.questionProgress.attempted}/${this.questionProgress.total}`;
      }
      if (key === 'notes') {
        return `${this.notesProgress.read}/${this.notesProgress.total}`;
      }
      if (key === 'mocks') {
        return `${this.mockProgress.attempted}/${this.mockProgress.total}`;
      }
      return '';
    },
    mockFlagCode(item) {
      if (!item) return '—';
      return item.number || item.code || item.question_no || '—';
    },
    resolveHref(location) {
      if (!this.$router || typeof this.$router.resolve !== 'function') return '';
      const resolved = this.$router.resolve(location);
      return resolved && resolved.href ? resolved.href : '';
    },
    linkedQuestionTarget(que) {
      if (!que) return { title: 'Chapter', entityId: null };
      if (que.sublist_id) return { title: 'Sublist', entityId: que.sublist_id };
      if (que.condition_id) return { title: 'Conditions', entityId: que.condition_id };
      if (que.presentation_id) return { title: 'Presentations', entityId: que.presentation_id };
      return { title: 'Chapter', entityId: que.subject_id };
    },
    openChartExpand(kind) {
      if (!kind) return;
      if (this.isOverviewScoreMode && (kind === 'combined' || kind === 'notes')) return;
      this.chartExpandKind = kind;
      this.chartExpandOpen = true;
      lockBodyScrollInline();
    },
    openPasswordConfirm(section, action) {
      if (!['reset', 'subscribe', 'unsubscribe', 'delete-account'].includes(action)) return;
      if (action !== 'delete-account' && !['questions', 'notes', 'mocks'].includes(section)) return;
      this.sectionResetAction = action;
      this.sectionResetKind = action === 'delete-account' ? '' : section;
      this.sectionResetPassword = '';
      this.sectionResetError = '';
      this.sectionResetSubmitting = false;
      this.sectionResetOpen = true;
      lockBodyScrollInline();
      this.$nextTick(() => {
        const el = this.$refs.sectionResetPasswordInput;
        if (el && typeof el.focus === 'function') el.focus();
      });
    },
    openSectionResetConfirm(section) {
      this.openPasswordConfirm(section, 'reset');
    },
    openSubscribeConfirm(section) {
      this.openPasswordConfirm(section, 'subscribe');
    },
    openUnsubscribeConfirm(section) {
      this.openPasswordConfirm(section, 'unsubscribe');
    },
    openDeleteAccountConfirm() {
      this.openPasswordConfirm('', 'delete-account');
    },
    openAccountEdit(field) {
      if (!['fullname', 'username', 'password'].includes(field)) return;
      this.accountEditField = field;
      this.accountEditValue = field === 'password'
        ? ''
        : String((this.profile && this.profile[field]) || '');
      this.accountEditCurrentPassword = '';
      this.accountEditPassword = '';
      this.accountEditPasswordConfirm = '';
      this.accountEditError = '';
      this.accountEditSubmitting = false;
      this.accountEditOpen = true;
      lockBodyScrollInline();
      this.$nextTick(() => {
        const el = this.$refs.accountEditFirstInput;
        if (el && typeof el.focus === 'function') el.focus();
      });
    },
    closeAccountEdit() {
      if (this.accountEditSubmitting) return;
      if (!this.accountEditOpen) return;
      this.accountEditOpen = false;
      this.accountEditField = '';
      this.accountEditValue = '';
      this.accountEditCurrentPassword = '';
      this.accountEditPassword = '';
      this.accountEditPasswordConfirm = '';
      this.accountEditError = '';
      if (!this.chartExpandOpen && !this.previewOpen && !this.sectionResetOpen) {
        releaseBodyScrollLock();
      }
    },
    confirmAccountEdit() {
      if (this.accountEditSubmitting || !this.accountEditCanSubmit) return;
      const field = this.accountEditField;
      const currentPassword = String(this.accountEditCurrentPassword || '');
      if (!currentPassword) {
        this.accountEditError = 'Please enter your current password.';
        return;
      }

      const payload = {
        field,
        current_password: currentPassword,
      };

      if (field === 'password') {
        const next = String(this.accountEditPassword || '');
        const confirm = String(this.accountEditPasswordConfirm || '');
        if (next.length < 6) {
          this.accountEditError = 'New password must be at least 6 characters.';
          return;
        }
        if (next !== confirm) {
          this.accountEditError = 'New passwords do not match.';
          return;
        }
        payload.password = next;
        payload.password_confirmation = confirm;
      } else {
        const value = String(this.accountEditValue || '').trim();
        if (!value) {
          this.accountEditError = field === 'username'
            ? 'Username is required.'
            : 'Full name is required.';
          return;
        }
        payload.value = value;
      }

      this.accountEditSubmitting = true;
      this.accountEditError = '';

      byMethod('POST', '/client-dashboard/update-profile', payload)
        .then((res) => {
          const updated = (res.data && res.data.user) || {};
          if (field === 'fullname' || field === 'username') {
            this.profile = {
              ...(this.profile || {}),
              [field]: updated[field] != null ? updated[field] : payload.value,
            };
          }
          if (this.user && typeof this.$store.dispatch === 'function') {
            this.$store.dispatch('user', {
              ...this.user,
              fullname: updated.fullname != null ? updated.fullname : this.user.fullname,
              username: updated.username != null ? updated.username : this.user.username,
            });
          }
          this.accountEditSubmitting = false;
          this.accountEditOpen = false;
          this.accountEditField = '';
          this.accountEditValue = '';
          this.accountEditCurrentPassword = '';
          this.accountEditPassword = '';
          this.accountEditPasswordConfirm = '';
          this.accountEditError = '';
          if (!this.chartExpandOpen && !this.previewOpen && !this.sectionResetOpen) {
            releaseBodyScrollLock();
          }
          this.subscribeMessage = field === 'password'
            ? 'Password updated successfully'
            : (field === 'username' ? 'Username updated successfully' : 'Full name updated successfully');
          this.showSubscribePopup = true;
        })
        .catch((err) => {
          const data = err && err.response && err.response.data;
          const msg = (data && (
            data.message
            || (data.errors && data.errors.value && data.errors.value[0])
            || (data.errors && data.errors.password && data.errors.password[0])
            || (data.errors && data.errors.current_password && data.errors.current_password[0])
          )) || 'Could not update right now. Please try again.';
          this.accountEditError = msg;
          this.accountEditSubmitting = false;
        });
    },
    closeSectionResetConfirm() {
      if (this.sectionResetSubmitting) return;
      if (!this.sectionResetOpen) return;
      this.sectionResetOpen = false;
      this.sectionResetAction = 'reset';
      this.sectionResetKind = '';
      this.sectionResetPassword = '';
      this.sectionResetError = '';
      if (!this.chartExpandOpen && !this.previewOpen && !this.accountEditOpen) releaseBodyScrollLock();
    },
    finishPasswordConfirmSuccess() {
      this.sectionResetSubmitting = false;
      this.sectionResetOpen = false;
      this.sectionResetAction = 'reset';
      this.sectionResetKind = '';
      this.sectionResetPassword = '';
      this.sectionResetError = '';
      if (!this.chartExpandOpen && !this.previewOpen && !this.accountEditOpen) releaseBodyScrollLock();
    },
    confirmSectionReset() {
      if (this.sectionResetSubmitting) return;
      const section = this.sectionResetKind;
      const action = this.sectionResetAction || 'reset';
      const password = String(this.sectionResetPassword || '');
      if (!password) {
        this.sectionResetError = 'Please enter your password.';
        return;
      }
      if (action !== 'delete-account' && !section) {
        this.sectionResetError = 'Please enter your password.';
        return;
      }
      this.sectionResetSubmitting = true;
      this.sectionResetError = '';

      if (action === 'delete-account') {
        // Frontend-only for now — no backend request.
        window.setTimeout(() => {
          this.finishPasswordConfirmSuccess();
          this.subscribeMessage = 'Need a admin approval will be done in 24-72 hrs';
          this.showSubscribePopup = true;
        }, 350);
        return;
      }

      if (action === 'subscribe' || action === 'unsubscribe') {
        byMethod('POST', '/client-dashboard/lock-section', {
          section,
          locked: action === 'unsubscribe',
          password,
        })
          .then((res) => {
            const locks = (res.data && res.data.section_locks) || {
              ...this.sectionLocks,
              [section]: action === 'unsubscribe',
            };
            const name = section === 'notes' ? 'Notes' : (section === 'mocks' ? 'Mocks' : 'Questions');
            this.applySectionLocks(locks);
            this.finishPasswordConfirmSuccess();
            this.subscribeMessage = action === 'subscribe'
              ? `Subscribed to ${name}`
              : `Unsubscribed from ${name}`;
            this.showSubscribePopup = true;
          })
          .catch((err) => {
            const msg = (err && err.response && err.response.data && err.response.data.message)
              || (action === 'subscribe'
                ? 'Could not subscribe right now. Please try again.'
                : 'Could not unsubscribe right now. Please try again.');
            this.sectionResetError = msg;
            this.sectionResetSubmitting = false;
          });
        return;
      }

      byMethod('POST', '/client-dashboard/reset-section', { section, password })
        .then(() => {
          if (section === 'notes') {
            clearAllNotesReadProgress(this.$store);
            invalidateNotesPrefsCache();
            this.notesReadRev += 1;
            hydrateNoteUserState();
          }
          this.finishPasswordConfirmSuccess();
          this.chartAnimKey += 1;
          this.loadDashboard();
          this.loadProgressSources();
        })
        .catch((err) => {
          const msg = (err && err.response && err.response.data && err.response.data.message)
            || 'Could not reset this section. Please try again.';
          this.sectionResetError = msg;
          this.sectionResetSubmitting = false;
        });
    },
    setOverviewMetricMode(mode) {
      const next = mode === 'score' ? 'score' : 'progress';
      if (this.overviewMetricMode === next) return;
      this.overviewMetricMode = next;
      this.chartAnimKey += 1;
      if (this.chartExpandOpen) this.closeChartExpand();
    },
    closeChartExpand() {
      if (!this.chartExpandOpen) return;
      this.chartExpandOpen = false;
      this.chartExpandKind = '';
      if (!this.previewOpen) releaseBodyScrollLock();
    },
    getFlagPreviewLocation(kind, item) {
      if (!item) return null;
      if (kind === 'note') {
        if (item.record_id == null) return null;
        const type = item.record_type === 'subnotes' ? 'subnotes' : 'notes';
        return {
          path: '/notespage',
          query: { id: String(item.record_id), type },
          _meta: {
            notestype: type,
            notestitle: item.title || item.number || 'Notes',
          },
        };
      }
      if (kind === 'mock') {
        if (item.mock_id == null) return null;
        const startId = item.question_id != null ? item.question_id : null;
        return {
          name: 'MockQuestionPreview',
          params: { id: item.mock_id },
          query: startId != null ? { startQuestionId: String(startId) } : {},
          _meta: {
            mockObject: {
              id: item.mock_id,
              title: item.mock_title || '',
              duration: item.mock_duration || 0,
            },
            mockStartQuestionId: startId != null ? String(startId) : null,
          },
        };
      }
      const questionTarget = this.linkedQuestionTarget(item);
      if (!questionTarget.entityId) return null;
      const query = {};
      if (item.code) query.startCode = item.code;
      return {
        name: 'QuestionsPage',
        params: { id: questionTarget.entityId },
        query,
        _meta: {
          questiontitle: questionTarget.title,
          questionStartCode: item.code || null,
        },
      };
    },
    applyFlagPreviewMeta(meta) {
      if (!meta) return;
      if (meta.notestype) localStorage.setItem('notestype', meta.notestype);
      if (meta.notestitle) localStorage.setItem('notestitle', meta.notestitle);
      if (meta.mockObject) {
        localStorage.setItem('question', 'normal');
        localStorage.setItem('mockObject', JSON.stringify(meta.mockObject));
      }
      if (meta.mockStartQuestionId != null) {
        localStorage.setItem('mockStartQuestionId', String(meta.mockStartQuestionId));
      }
      if (meta.questiontitle) localStorage.setItem('questiontitle', meta.questiontitle);
      if (meta.questionStartCode) localStorage.setItem('questionStartCode', meta.questionStartCode);
    },
    buildFlagPreviewLocation(kind, item, embedded = true) {
      const base = this.getFlagPreviewLocation(kind, item);
      if (!base) return null;
      this.applyFlagPreviewMeta(base._meta);
      const query = { ...(base.query || {}) };
      if (embedded) query.embeddedPreview = '1';
      // Open matching NLQ accordion tab when previewing from Comments / Journals
      if (this.activeSection === 'comments') query.openTab = 'comments';
      else if (this.activeSection === 'journals') query.openTab = 'journal';
      return {
        name: base.name,
        path: base.path,
        params: base.params,
        query,
      };
    },
    buildFlagPreviewSrc(kind, item) {
      const location = this.buildFlagPreviewLocation(kind, item, true);
      return location ? this.resolveHref(location) : '';
    },
    openFlagPreview(kind, item) {
      if (!item) return;
      if (kind === 'note' && this.isSectionLocked('notes')) return;
      if (kind === 'mock' && this.isSectionLocked('mocks')) return;
      if (kind === 'question' && this.isSectionLocked('questions')) return;

      this.previewKind = kind;
      this.previewItem = item;
      this.previewError = '';
      this.previewSrc = this.buildFlagPreviewSrc(kind, item);
      if (!this.previewSrc) {
        this.previewError = `Could not open this ${kind}.`;
      }
      this.previewOpen = true;
      lockBodyScrollInline();
    },
    closeFlagPreview() {
      if (!this.previewOpen && !this.previewSrc && !this.previewError) return;
      this.previewOpen = false;
      this.previewKind = '';
      this.previewItem = null;
      this.previewSrc = '';
      this.previewError = '';
      releaseBodyScrollLock();
    },
    goToPreviewItem() {
      const location = this.buildFlagPreviewLocation(this.previewKind, this.previewItem, false);
      if (!location) return;
      this.closeFlagPreview();
      this.$router.push(location);
    },
    go(path) {
      this.menuOpen = false;
      this.$router.push(path);
    },
    goSection(section, path) {
      this.menuOpen = false;
      if (this.isSectionLocked(section)) {
        this.openSubscribePopup(section);
        return;
      }
      this.$router.push(path);
    },
    openSubscribePopup(section) {
      // Locked-section navigation still shows info popup;
      // Settings SUBSCRIBE uses openSubscribeConfirm (password).
      this.subscribeMessage = subscribeSectionMessage(section);
      this.showSubscribePopup = true;
    },
    applySectionLocks(locks) {
      const next = {
        questions: !!(locks && locks.questions),
        notes: !!(locks && locks.notes),
        mocks: !!(locks && locks.mocks),
      };
      this.sectionLocks = next;
      setSectionLocksCache(next);
    },
    triggerProfilePick() {
      if (this.profileUploading) return;
      this.profileUploadError = '';
      const input = this.$refs.profileFileInput;
      if (input) input.click();
    },
    onProfileFileSelected(ev) {
      const file = ev && ev.target && ev.target.files && ev.target.files[0];
      if (ev && ev.target) ev.target.value = '';
      if (!file) return;
      if (!/^image\//i.test(file.type || '')) {
        this.profileUploadError = 'Please choose an image file.';
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.profileUploadError = 'Image must be 5MB or smaller.';
        return;
      }
      this.uploadProfilePicture(file);
    },
    uploadProfilePicture(file) {
      this.profileUploading = true;
      this.profileUploadError = '';
      const formData = new FormData();
      formData.append('profile', file);
      byMethod('POST', '/client-dashboard/profile-picture', formData)
        .then((res) => {
          const filename = (res.data && res.data.profile) || '';
          this.profile = { ...(this.profile || {}), profile: filename };
          if (this.user && typeof this.$store.dispatch === 'function') {
            this.$store.dispatch('user', { ...this.user, profile: filename });
          }
          this.profileAvatarCacheBust = Date.now();
          this.profileUploading = false;
        })
        .catch((err) => {
          const msg = (err && err.response && err.response.data && (
            err.response.data.message
            || (err.response.data.errors && err.response.data.errors.profile && err.response.data.errors.profile[0])
          )) || 'Could not upload profile picture.';
          this.profileUploadError = msg;
          this.profileUploading = false;
        });
    },
    logout() {
      this.menuOpen = false;
      clearSectionLocksCache();
      try {
        sessionStorage.removeItem('examReminderPending');
      } catch (e) {
        /* ignore */
      }
      localStorage.removeItem('token');
      this.$store.dispatch('user', null);
      this.$router.push('/');
    },
    maybeShowExamReminder() {
      let pending = false;
      try {
        pending = sessionStorage.getItem('examReminderPending') === '1';
      } catch (e) {
        pending = false;
      }
      if (!pending || !this.isExamUrgent) {
        this.examReminderOpen = false;
        return;
      }
      this.examReminderOpen = true;
    },
    dismissExamReminder() {
      this.examReminderOpen = false;
      try {
        sessionStorage.removeItem('examReminderPending');
      } catch (e) {
        /* ignore */
      }
    },
    formatDate(val) {
      if (!val) return '';
      try {
        return new Date(val).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
      } catch (e) {
        return String(val);
      }
    },
    examDateStorageKey() {
      const id = (this.user && this.user.id) || (this.profile && this.profile.id) || 'anon';
      return `dashboardExamDate_${id}`;
    },
    loadExamDate() {
      try {
        const saved = localStorage.getItem(this.examDateStorageKey());
        this.examDateIso = saved || null;
      } catch (e) {
        this.examDateIso = null;
      }
    },
    saveExamDate(iso) {
      this.examDateIso = iso || null;
      try {
        if (iso) {
          localStorage.setItem(this.examDateStorageKey(), iso);
        } else {
          localStorage.removeItem(this.examDateStorageKey());
        }
      } catch (e) {
        /* ignore */
      }
      this.maybeShowExamReminder();
    },
    formatExamDisplayDate(iso) {
      if (!iso) return '—';
      const parts = String(iso).split('-').map(Number);
      if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return iso;
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const day = String(parts[2]).padStart(2, '0');
      const mon = months[parts[1] - 1] || parts[1];
      const yy = String(parts[0]).slice(-2);
      return `${day}-${mon}-${yy}`;
    },
    toggleExamPanel() {
      this.examPanelOpen = !this.examPanelOpen;
    },
    onExamDateChange(ev) {
      const val = ev && ev.target ? ev.target.value : '';
      this.saveExamDate(val || null);
      if (val) {
        this.examPanelOpen = true;
      }
    },
    transformNotesChapters(chapters) {
      if (!chapters || !Array.isArray(chapters)) return [];
      return chapters.map((chapter) => {
        const transformed = {
          id: chapter.id || null,
          title: chapter.title || '',
          index_number: chapter.index_number || null,
        };
        if (chapter.notes && Array.isArray(chapter.notes) && chapter.notes.length) {
          transformed.children = chapter.notes.map((note) => {
            const n = {
              id: note.id || null,
              title: note.title || '',
              number: note.number || null,
            };
            if (note.sub_notes && note.sub_notes.length) {
              n.children = note.sub_notes.map((sub) => ({
                id: sub.id || null,
                title: sub.title || '',
                number: sub.number || null,
              }));
            }
            return n;
          });
        }
        return transformed;
      });
    },
    loadProgressSources() {
      get('/getsubjectclient').then((res) => {
        this.clinicalAreas = res.data.clinical || [];
        this.professionalKnowledge = res.data.professional || [];
      }).catch(() => { /* ignore */ });

      get('/notesclient').then((res) => {
        if (res.data.chaptersClinical) {
          this.notesClinicalAreas = this.transformNotesChapters(res.data.chaptersClinical);
        }
        if (res.data.chaptersProfessioanl) {
          this.notesProfessionalKnowledge = this.transformNotesChapters(res.data.chaptersProfessioanl);
        }
        rebuildNotesNumberRegistry([
          { items: this.notesClinicalAreas, listMode: 'chapter' },
          { items: this.notesProfessionalKnowledge, listMode: 'chapter' },
        ]);
        this.notesReadRev += 1;
      }).catch(() => { /* ignore */ });

      get('/getmocks').then((res) => {
        this.mocksData = res.data.data || [];
      }).catch(() => { /* ignore */ });
    },
    loadDashboard() {
      this.loading = true;
      get('/client-dashboard')
        .then((res) => {
          const d = res.data || {};
          this.profile = d.user || {};
          this.loadExamDate();
          this.subscription = d.subscription || this.subscription;
          this.stats = d.stats || { ...EMPTY_STATS };
          this.applyChartTrends(d.chart_trends);
          this.flaggedQuestions = d.flagged_questions || [];
          this.flaggedMocks = d.flagged_mocks || [];
          this.flaggedNotes = d.flagged_notes || [];
          this.comments = d.comments || [];
          this.journals = d.journals || [];
          this.feedbackQuestions = d.feedback_questions || [];
          this.feedbackMocks = d.feedback_mocks || [];
          this.feedbackNotes = d.feedback_notes || [];
          const locks = d.section_locks || {};
          this.applySectionLocks(locks);
          this.loaded = true;
          this.chartAnimKey += 1;
          this.loadLocalJournals();
        })
        .catch(() => {
          if (this.user) {
            this.profile = {
              fullname: this.user.fullname,
              username: this.user.username,
              email: this.user.email,
            };
          }
          this.applyChartTrends(null);
          this.chartAnimKey += 1;
          this.loadLocalJournals();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    applyChartTrends(trends) {
      const t = trends && typeof trends === 'object' ? trends : {};
      const weeks = Array.isArray(t.weeks) && t.weeks.length
        ? t.weeks.map((w) => Number(w)).filter((n) => Number.isFinite(n))
        : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
      this.progressTrendWeeks = weeks.length ? weeks : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
      this.combinedQuestionsProgress = Array.isArray(t.questions_progress) ? t.questions_progress : [];
      this.combinedMocksProgress = Array.isArray(t.mocks_progress) ? t.mocks_progress : [];
      this.questionsScoreTrend = Array.isArray(t.questions_score) ? t.questions_score : [];
      this.scorePassingPercent = Number(t.passing_score) > 0 ? Number(t.passing_score) : 70;
      this.scoreAveragePercent = Number(t.average_score) >= 0 ? Number(t.average_score) : 60;
      // Notes progress is derived locally in notesProgressTrendPoints
      this.combinedNotesProgress = [];

      // Fallback if API has no weekly history yet but current stats exist
      const q = this.stats.questions || {};
      const m = this.stats.mocks || {};
      if (!this.combinedQuestionsProgress.length && (Number(q.attempted) || 0) > 0) {
        this.combinedQuestionsProgress = [{ week: 1, progress: Math.round(Number(q.percent) || 0) }];
      }
      if (!this.questionsScoreTrend.length && (Number(q.correct) + Number(q.incorrect)) > 0) {
        this.questionsScoreTrend = [{ week: 1, score: Math.round(Number(q.score_percent) || 0) }];
      }
      if (!this.combinedMocksProgress.length && (Number(m.attempted) || 0) > 0) {
        this.combinedMocksProgress = [{ week: 1, progress: Math.round(Number(m.percent) || 0) }];
      }
    },
    journalPreviewFromHtml(html) {
      const plain = String(html || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (!plain) return '';
      return plain.length > 120 ? `${plain.slice(0, 120)}…` : plain;
    },
    scanLocalJournalEntries(kind) {
      const uid = (this.user && this.user.id) || (this.profile && this.profile.id) || 'anon';
      const prefix = `synapse_journal_${kind}_${uid}_`;
      const entries = [];
      try {
        for (let i = 0; i < localStorage.length; i += 1) {
          const key = localStorage.key(i);
          if (!key || key.indexOf(prefix) !== 0 || key.indexOf('__meta') !== -1) continue;
          const idStr = key.slice(prefix.length);
          if (!/^\d+$/.test(idStr)) continue;
          const preview = this.journalPreviewFromHtml(localStorage.getItem(key) || '');
          if (!preview) continue;
          let meta = {};
          try {
            const rawMeta = localStorage.getItem(`${key}__meta`);
            if (rawMeta) meta = JSON.parse(rawMeta) || {};
          } catch (e) { /* ignore */ }
          entries.push({
            id: Number(idStr),
            question_id: Number(idStr),
            preview,
            ...meta,
          });
        }
      } catch (e) { /* ignore */ }
      return entries;
    },
    loadLocalJournals() {
      const questionEntries = this.scanLocalJournalEntries('question');
      const mockEntries = this.scanLocalJournalEntries('mock');
      this.journalsQuestions = questionEntries;
      this.journalsMocks = mockEntries;

      const questionIds = questionEntries.map((e) => e.id).filter(Boolean);
      const mockIds = mockEntries.map((e) => e.id).filter(Boolean);
      if (!questionIds.length && !mockIds.length) return;

      get('/client-dashboard/journal-targets', {
        question_ids: questionIds.join(','),
        mock_ids: mockIds.join(','),
      })
        .then((res) => {
          const qById = {};
          const mById = {};
          (res.data.questions || []).forEach((q) => { qById[q.id] = q; });
          (res.data.mocks || []).forEach((m) => { mById[m.id] = m; });
          this.journalsQuestions = questionEntries.map((e) => ({
            ...e,
            ...(qById[e.id] || {}),
            preview: e.preview,
          }));
          this.journalsMocks = mockEntries.map((e) => ({
            ...e,
            ...(mById[e.id] || {}),
            preview: e.preview,
          }));
        })
        .catch(() => { /* keep local stubs */ });
    },
  },
};
</script>

<style scoped>
.dashboard-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #f0f7ff 0%, #f8fbff 40%, #fff 100%);
  padding: 16px 16px 40px;
  text-align: left;
}

.dash-topbar {
  max-width: 1280px;
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.dash-topbar-logo img {
  height: 42px;
  width: auto;
  display: block;
}

.dash-topbar-actions {
  position: relative;
}

.dash-menu-btn {
  width: 42px;
  height: 42px;
  border: 1px solid #dbe7f3;
  border-radius: 12px;
  background: #fff;
  color: #556;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(26, 144, 255, 0.08);
}

.dash-menu-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: auto;
  min-width: 180px;
  margin: 0;
  padding: 8px 0;
  list-style: none;
  background: #fff;
  border: 1px solid #dbe7f3;
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
  z-index: 20;
}

.dash-menu-dropdown li a {
  display: block;
  padding: 10px 16px;
  color: #333;
  text-decoration: none;
  font-size: 14px;
}

.dash-menu-dropdown li a:hover {
  background: #f0f7ff;
  color: #1a90ff;
}

.dashboard-shell {
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 20px;
  align-items: start;
}

/* Sidebar column */
.dash-sidebar-col {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

/* Sidebar */
.dash-sidebar {
  background: #fff;
  border-radius: 20px;
  padding: 24px 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
}

.dash-profile-card {
  text-align: center;
  padding-bottom: 20px;
  border-bottom: 1px solid #eef2f7;
  margin-bottom: 16px;
}

.dash-avatar-wrap {
  position: relative;
  width: 88px;
  margin: 0 auto 12px;
}

.dash-avatar-ring {
  width: 88px;
  height: 88px;
  margin: 0;
  border-radius: 50%;
  background: conic-gradient(#9ded6c 0 var(--p, 33%), #d7ebff var(--p, 33%) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
}

.dash-avatar {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: #1a90ff;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 700;
  font-family: 'Helveticacondensed', Helvetica, sans-serif;
  overflow: hidden;
}

.dash-avatar.has-photo {
  background: #d7ebff;
}

.dash-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.dash-avatar-edit {
  position: absolute;
  right: -2px;
  bottom: 2px;
  width: 28px;
  height: 28px;
  border: 2px solid #fff;
  border-radius: 50%;
  background: #1a90ff;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  box-shadow: 0 2px 8px rgba(26, 144, 255, 0.35);
  z-index: 2;
}

.dash-avatar-edit:hover {
  background: #0d7de0;
}

.dash-avatar-edit:disabled {
  opacity: 0.65;
  cursor: default;
}

.dash-avatar-file {
  display: none;
}

.dash-avatar-error {
  margin: -4px 0 8px;
  font-size: 11px;
  font-weight: 600;
  color: #ed1c24;
}

.dash-welcome {
  margin: 0;
  font-size: 12px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.dash-name {
  margin: 4px 0 10px;
  font-size: 18px;
  color: #231f20;
  font-weight: 700;
}

.dash-badge {
  display: inline-block;
  background: #e8f8e0;
  color: #2e7d32;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
}

.dash-quick-icons {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 14px;
}

.dash-icon-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: #f0f7ff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.dash-lock-icon {
  width: 12px;
  height: 12px;
  margin-left: 6px;
  vertical-align: middle;
}

.dash-lock-icon-sm {
  position: absolute;
  right: -2px;
  bottom: -2px;
  width: 12px;
  height: 12px;
}

.is-locked-link {
  opacity: 0.75;
  cursor: not-allowed !important;
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: block;
}
.dot-q { background: #1a90ff; }
.dot-n { background: #f59c00; }
.dot-m { background: #ed1c24; }

.dash-nav {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dash-nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 12px;
  cursor: pointer;
  font-size: 14px;
  color: #6d6e71;
  text-align: left;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.dash-nav-item:hover {
  background: #f3f8ff;
  color: #1a90ff;
}

.dash-nav-item.active {
  background: #d7ebff;
  color: #1a90ff;
  font-weight: 600;
  border-color: #8fc7ff;
}

.dash-nav-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: inherit;
}

.dash-nav-icon ::v-deep svg {
  display: block;
  width: 18px;
  height: 18px;
}

.dash-exam-widget {
  position: relative;
  width: 100%;
  padding-top: 10px;
}

.dash-exam-banner {
  display: flex;
  align-items: stretch;
  width: 100%;
  background: #eceff3;
  border-radius: 999px;
  overflow: hidden;
  box-shadow: none;
  border: 1px solid #dde3ea;
}

.dash-exam-banner.open {
  border-radius: 18px 18px 0 0;
}

.dash-exam-banner-main {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  color: #5a6570;
  font-size: 12px;
  font-weight: 700;
  text-align: left;
  padding: 11px 8px 11px 14px;
  cursor: pointer;
  line-height: 1.35;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.dash-exam-inline-cal {
  flex-shrink: 0;
  color: #6d6e71;
}

.dash-exam-cal-btn {
  position: relative;
  flex-shrink: 0;
  border-left: 1px solid #dde3ea;
  background: transparent;
  color: #6d6e71;
  width: 46px;
  min-height: 44px;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  overflow: hidden;
}

.dash-exam-cal-btn:hover {
  background: rgba(0, 0, 0, 0.04);
}

.dash-exam-cal-icon {
  pointer-events: none;
}

.dash-exam-date-input-overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  border: none;
  opacity: 0;
  cursor: pointer;
  z-index: 2;
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  color: transparent;
}

.dash-exam-date-input-overlay::-webkit-calendar-picker-indicator {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  cursor: pointer;
  opacity: 0;
}

.dash-exam-countdown {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 10px 14px;
  background: #fff;
  border: 1px solid #dde3ea;
  border-top: none;
  border-radius: 0 0 18px 18px;
}

.dash-exam-date-line {
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  color: #7b6e75;
  line-height: 1.3;
}

.dash-exam-date-line.is-exam-urgent,
.dash-exam-banner-main span.is-exam-urgent {
  color: #ed1c24;
}

.dash-exam-reminder-overlay {
  position: fixed;
  inset: 0;
  z-index: 10070;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(35, 31, 32, 0.45);
  box-sizing: border-box;
}

.dash-exam-reminder-modal {
  position: relative;
  width: min(340px, 100%);
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.22);
  padding: 28px 22px 22px;
  text-align: center;
  border: 1px solid #ed1c24;
}

.dash-exam-reminder-close {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #6d6e71;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.dash-exam-reminder-close:hover {
  background: #f3f3f3;
  color: #231f20;
}

.dash-exam-reminder-kicker {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #ed1c24;
}

.dash-exam-reminder-date {
  margin: 0 0 10px;
  font-size: 16px;
  font-weight: 700;
  color: #231f20;
}

.dash-exam-reminder-days {
  margin: 0 0 18px;
  font-size: 22px;
  font-weight: 800;
  color: #ed1c24;
  line-height: 1.2;
}

.dash-exam-reminder-ok {
  appearance: none;
  border: none;
  border-radius: 10px;
  background: #ed1c24;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  padding: 10px 22px;
  cursor: pointer;
}

.dash-exam-reminder-ok:hover {
  background: #c9161d;
}

.dash-exam-count-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.dash-exam-count-col {
  text-align: center;
}

.dash-exam-count-label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: #6d6e71;
  margin-bottom: 4px;
}

.dash-exam-count-value {
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: #231f20;
  line-height: 1.1;
}

.dash-exam-count-value--time {
  font-size: 14px;
  letter-spacing: -0.02em;
}

.dash-exam-expand-enter-active,
.dash-exam-expand-leave-active {
  transition: max-height 0.35s ease, opacity 0.3s ease, transform 0.35s ease;
  overflow: hidden;
  transform-origin: top center;
}

.dash-exam-expand-enter,
.dash-exam-expand-leave-to {
  max-height: 0;
  opacity: 0;
  transform: scaleY(0.92);
}

.dash-exam-expand-enter-to,
.dash-exam-expand-leave {
  max-height: 160px;
  opacity: 1;
  transform: scaleY(1);
}

/* Main */
.dash-main {
  background: #f8fbff;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1);
  min-height: 600px;
}

.dash-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;
}

.dash-header h1 {
  margin: 0 0 4px;
  font-size: 26px;
  color: #231f20;
}

.dash-header p {
  margin: 0;
  color: #666;
  font-size: 14px;
}

.dash-refresh-btn {
  background: #1a90ff;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 10px 18px;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
}

.dash-refresh-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.dash-loading,
.dash-empty {
  color: #888;
  font-size: 14px;
  padding: 20px 0;
}

/* Performance: Q / N / M progress cards (colored header + line chart) */
.dash-metric-toggle {
  display: inline-flex;
  padding: 3px;
  border-radius: 999px;
  background: #eef3f8;
  border: 1px solid #d7e2ee;
  gap: 2px;
}

.dash-metric-toggle__btn {
  border: none;
  background: transparent;
  color: #5a6570;
  font-size: 13px;
  font-weight: 700;
  font-family: Helvetica, Arial, sans-serif;
  padding: 7px 16px;
  border-radius: 999px;
  cursor: pointer;
  line-height: 1.2;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.dash-metric-toggle__btn.is-active {
  background: #1a90ff;
  color: #fff;
  box-shadow: 0 2px 8px rgba(26, 144, 255, 0.28);
}

.dash-metric-toggle__btn:focus-visible {
  outline: 2px solid #1a90ff;
  outline-offset: 2px;
}

.dash-overview-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 20px;
  align-items: stretch;
}

/* Two cards, each same width as a Progress-mode card; centered as a group */
.dash-overview-grid--score {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  width: calc((100% - 32px) * 2 / 3 + 16px);
  max-width: 100%;
  margin-left: auto;
  margin-right: auto;
}

.dash-section-progress-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e8eef5;
  box-shadow: 0 8px 24px rgba(26, 144, 255, 0.06);
  display: flex;
  flex-direction: column;
  min-height: 100%;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  padding: 10px;
}

.dash-section-progress-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(26, 144, 255, 0.1);
}

/* Same Q/N/M progress cards as Settings / homepage */
.dash-overview-progress-box.mla-question-box {
  background: #1a90ff;
  margin: 0;
  padding: 0;
  border-radius: 15px;
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  overflow: hidden;
  text-align: center;
  box-sizing: border-box;
  width: 100%;
  min-height: 75px;
  border: none;
  cursor: default;
}

.dash-overview-progress-box.mla-note-box {
  background-color: #f59c00;
}

.dash-overview-progress-box.mla-mock-box {
  background-color: #ed1c24;
}

.dash-overview-progress-box .section-card-body {
  flex: 1;
  min-width: 0;
  padding: 10px 12px 8px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.dash-overview-progress-box .cardbottom-shadow img {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: -27px;
  width: 280px;
  height: auto;
  z-index: -1;
  pointer-events: none;
}

.dash-overview-progress-box h4 {
  margin: 0;
  font-size: 20px;
  color: #fff;
  text-transform: uppercase;
  padding: 0 0 6px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  font-family: Helvetica, sans-serif;
  font-weight: 800;
  letter-spacing: 0.03em;
}

.dash-overview-progress-box h4 img {
  width: 26px;
  height: 26px;
  object-fit: contain;
  flex-shrink: 0;
}

.dash-overview-progress-box ::v-deep .progress-wrapper {
  color: #fff;
  padding: 0 4px;
}

.dash-overview-progress-box ::v-deep .label {
  color: #fff;
  font-weight: 700;
}

.dash-overview-progress-box ::v-deep .progress-bar {
  border: 2px solid #ffffff;
  background: transparent;
  height: 9px;
  border-radius: 10px;
}

.dash-overview-progress-box ::v-deep .progress-fill {
  background-color: #9ded6c;
}

.dash-overview-progress-box ::v-deep .percentage {
  color: #fff;
  font-weight: 600;
}

.dash-section-progress-body {
  position: relative;
  flex: 1 1 auto;
  padding: 12px 10px 28px;
  cursor: zoom-in;
  background: #fff;
}

.dash-section-progress-body ::v-deep .score-trend-chart {
  width: 100%;
  max-width: 100%;
  margin-left: auto;
  margin-right: auto;
}

.dash-overview-grid--score .dash-section-progress-body {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.dash-section-progress-body ::v-deep .score-trend-legend {
  font-size: 11px;
  gap: 8px 14px;
}

.dash-section-progress-body:focus {
  outline: 2px solid #1a90ff;
  outline-offset: -2px;
}

.dash-section-progress-card--q .dash-section-progress-body { background: #f3f9ff; }
.dash-section-progress-card--n .dash-section-progress-body { background: #fffaf0; }
.dash-section-progress-card--m .dash-section-progress-body { background: #fff5f5; }

.dash-section-progress-refresh {
  position: absolute;
  right: 12px;
  bottom: 8px;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  padding: 0;
  z-index: 2;
  pointer-events: auto;
}

.dash-section-progress-refresh:hover {
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.dash-section-progress-card--q .dash-section-progress-refresh { color: #1a90ff; }
.dash-section-progress-card--n .dash-section-progress-refresh { color: #f59c00; }
.dash-section-progress-card--m .dash-section-progress-refresh { color: #ed1c24; }

.dash-section-reset-modal {
  width: min(440px, calc(100vw - 32px));
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.22);
  overflow: hidden;
}

.dash-section-reset-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-top: 4px solid #1a90ff;
  border-bottom: 1px solid #eef2f7;
}

.dash-section-reset-header h3 {
  margin: 0;
  font-size: 16px;
  color: #231f20;
}

.dash-section-reset-body {
  padding: 16px;
}

.dash-section-reset-message {
  margin: 0 0 14px;
  font-size: 13px;
  line-height: 1.45;
  color: #444;
}

.dash-section-reset-label {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #555;
}

.dash-section-reset-input + .dash-section-reset-label {
  margin-top: 12px;
}

.dash-section-reset-input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #d7e2ee;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 14px;
  margin-bottom: 8px;
}

.dash-section-reset-input:focus {
  outline: 2px solid #1a90ff;
  outline-offset: 1px;
}

.dash-section-reset-error {
  margin: 0 0 10px;
  font-size: 12px;
  font-weight: 600;
  color: #ed1c24;
}

.dash-section-reset-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
}

.dash-section-reset-cancel,
.dash-section-reset-confirm {
  border: none;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.dash-section-reset-cancel {
  background: #eef3f8;
  color: #555;
}

.dash-section-reset-confirm {
  background: #ed1c24;
  color: #fff;
}

.dash-section-reset-confirm:disabled,
.dash-section-reset-cancel:disabled {
  opacity: 0.6;
  cursor: default;
}

.dash-combined-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e8eef5;
  border-top: 4px solid #6d6e71;
  box-shadow: 0 8px 24px rgba(26, 144, 255, 0.06);
  margin-bottom: 20px;
  overflow: hidden;
}

.dash-chart-block--combined {
  padding: 16px 16px 18px;
}

.dash-chart-block {
  padding: 14px 12px 12px;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.dash-chart-block--clickable {
  cursor: zoom-in;
  border-radius: 0 0 12px 12px;
  transition: background 0.15s ease;
}

.dash-chart-block--clickable:hover {
  background: #f7fbff;
}

.dash-chart-block--clickable:focus {
  outline: 2px solid #1a90ff;
  outline-offset: -2px;
}

.dash-chart-expand-modal {
  width: min(860px, 100%);
  max-height: calc(100vh - 80px);
  overflow: hidden;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.28);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.dash-chart-expand-body {
  padding: 16px 24px 20px;
  overflow: auto;
  background: #fff;
}

.dash-panel-head {
  margin-bottom: 6px;
  text-align: center;
}

.dash-panel-head h3 {
  margin: 0 0 2px;
  font-size: 14px;
  color: #231f20;
}

.dash-panel-head p {
  margin: 0;
  font-size: 11px;
  color: #888;
}

.dash-panel {
  background: #fff;
  border-radius: 16px;
  padding: 18px;
  border: 1px solid #e8eef5;
  box-shadow: 0 8px 24px rgba(26, 144, 255, 0.05);
}

.dash-panel--accent {
  overflow: hidden;
}

.dash-panel--q { border-top: 4px solid #1a90ff; }
.dash-panel--n { border-top: 4px solid #f59c00; }
.dash-panel--m { border-top: 4px solid #ed1c24; }

.dash-panel h3 {
  margin: 0 0 16px;
  font-size: 15px;
  color: #231f20;
}

.dash-panel h3 .count {
  font-weight: 400;
  color: #888;
  font-size: 13px;
}

/* Info row */
.dash-info-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.dash-info-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.dash-info-list li {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #f0f3f8;
  font-size: 13px;
  gap: 12px;
}

.dash-info-list strong {
  color: #666;
  font-weight: 500;
}

.dash-account-row .dash-account-value {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-align: right;
  min-width: 0;
}

.dash-account-value > span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 220px;
}

.dash-account-edit {
  appearance: none;
  border: none;
  background: #eef5ff;
  color: #1a90ff;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.dash-account-edit:hover {
  background: #1a90ff;
  color: #fff;
  box-shadow: 0 2px 8px rgba(26, 144, 255, 0.28);
}

.dash-account-edit:focus-visible {
  outline: 2px solid #1a90ff;
  outline-offset: 2px;
}

.dash-delete-account-row {
  justify-content: flex-start;
  border-bottom: none;
  padding-top: 12px;
}

.dash-delete-account-link {
  appearance: none;
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  color: #666;
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  text-align: left;
}

.dash-delete-account-link:hover {
  color: #ed1c24;
  text-decoration: underline;
}

.dash-sub-card {
  background: linear-gradient(135deg, #1a90ff, #0d6ecc);
  color: #fff;
  border-radius: 14px;
  padding: 18px;
  margin-bottom: 16px;
}

.dash-sub-card--plan {
  background: linear-gradient(135deg, #9b9b9b, #5d5d5d);
  margin-top: 6px;
  margin-bottom: 14px;
}

.dash-sub-plan {
  display: block;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 6px;
}

.dash-sub-status {
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}

.dash-sub-status.active {
  background: #9ded6c;
  color: #1b5e20;
}

.dash-sub-since {
  margin: 10px 0 0;
  font-size: 12px;
  opacity: 0.9;
}

.dash-sub-panel {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.dash-settings-cards {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.dash-sub-row {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(130px, 0.75fr) minmax(120px, 0.7fr);
  gap: 14px;
  align-items: center;
}

/* Match homepage / UserLogin card CSS exactly */
.dash-settings-cards .mla-question-box {
  background: #1a90ff;
  border: 1px solid #0276ba;
  margin: 0;
  padding: 0;
  border-radius: 16px;
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  overflow: hidden;
  text-align: center;
  box-sizing: border-box;
  width: 100%;
  min-height: 60px;
  cursor: pointer;
  transition: transform 0.25s ease;
  border: none;
}

.dash-settings-cards .mla-question-box .section-card-body {
  flex: 1;
  min-width: 0;
  padding: 8px 10px 0px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.dash-settings-cards .mla-question-box .cardbottom-shadow img {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: -27px;
  width: 280px;
  height: auto;
  z-index: -1;
  pointer-events: none;
}

.dash-settings-cards .mla-question-box h4 {
  margin: 0;
  font-size: 13px;
  color: #fff;
  text-transform: uppercase;
  padding: 0 0 6px 0;
  display: flex;
  align-items: center;
  gap: 3px;
  justify-content: center;
  font-family: Helvetica, sans-serif;
}

.dash-settings-cards .mla-question-box h4 img {
  width: 20px;
  height: 20px;
  object-fit: contain;
  flex-shrink: 0;
}

.dash-settings-cards .mla-note-box {
  background-color: #f59c00;
  border-color: #f59c00;
  border: none;
}

.dash-settings-cards .mla-mock-box {
  background-color: #ed1c24;
  border-color: #ed1c24;
  border: none;
}

.dash-settings-cards .mla-question-box:hover {
  transform: translateY(-4px);
}

.dash-settings-cards .mla-question-box.is-section-locked {
  cursor: not-allowed;
  min-height: 60px;
}

.dash-settings-cards .mla-question-box.is-section-locked:hover {
  transform: none;
}

.dash-settings-cards .mla-question-box.is-section-locked .section-card-body {
  justify-content: center;
  min-height: 60px;
}

.dash-settings-cards .mla-question-box ::v-deep .progress-wrapper {
  padding: 0 4px 2px;
  font-size: 13px;
}

.dash-sub-section-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 88px;
  padding: 0 4px;
}

.dash-sub-section-action {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 88px;
  padding: 0 4px;
}

.dash-subscribe-btn {
  border: none;
  background: #ed1c24;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 12px 18px;
  border-radius: 10px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(237, 28, 36, 0.28);
  white-space: nowrap;
  min-width: 132px;
  text-align: center;
}

.dash-unsubscribe-btn {
  background: #6d6e71;
  box-shadow: 0 4px 12px rgba(109, 110, 113, 0.28);
}

.dash-subscribed-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
}

.dash-subscribed-wrap.is-not-subscribed .dash-subscribed-text {
  color: #9a9a9a;
}

.dash-subscribed-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-size: 12px;
  font-weight: 700;
  color: #444;
  letter-spacing: 0.03em;
  line-height: 1.25;
}

.dash-subscribed-text small {
  font-size: 11px;
  font-weight: 600;
  color: #777;
  letter-spacing: 0;
}

.dash-subscribed-check {
  display: inline-flex;
  flex-shrink: 0;
  display: none;
}

.dash-settings-logout {
  width: 100%;
  border: 1px solid #ffcdd2;
  border-radius: 10px;
  background: #fff5f5;
  color: #c62828;
  font-size: 14px;
  font-weight: 700;
  padding: 12px 14px;
  cursor: pointer;
  text-align: center;
}

.dash-settings-logout:hover {
  background: #ffeaea;
  border-color: #ef9a9a;
}

.dash-mini-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.dash-mini-stats div {
  background: #f0f7ff;
  border-radius: 10px;
  padding: 10px;
  text-align: center;
}

.dash-mini-stats strong {
  display: block;
  font-size: 20px;
  color: #1a90ff;
}

.dash-mini-stats span {
  font-size: 10px;
  color: #666;
}

/* Lists */
.dash-list-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.dash-panel--wide {
  grid-column: 1 / -1;
}

.dash-list,
.dash-comment-list,
.dash-journal-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 420px;
  overflow-y: auto;
}

.dash-list li,
.dash-comment-list li,
.dash-journal-list li {
  padding: 12px 0;
  border-bottom: 1px solid #f0f3f8;
}

.dash-list-code {
  display: inline-block;
  min-width: 64px;
  font-size: 12px;
  font-weight: 700;
  color: #1a90ff;
  margin-right: 8px;
}

.dash-list-code--btn {
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  text-align: left;
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  color: #1a90ff;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.dash-list-code--btn:hover {
  color: #0d6ecc;
}

.dash-panel--q .dash-list-code,
.dash-panel--q .dash-list-code--btn {
  color: #1a90ff;
}

.dash-panel--q .dash-list-code--btn:hover {
  color: #0d6ecc;
}

.dash-panel--n .dash-list-code,
.dash-panel--n .dash-list-code--btn {
  color: #f59c00;
}

.dash-panel--n .dash-list-code--btn:hover {
  color: #c47d00;
}

.dash-panel--m .dash-list-code,
.dash-panel--m .dash-list-code--btn {
  color: #ed1c24;
}

.dash-panel--m .dash-list-code--btn:hover {
  color: #b31218;
}

.dash-list-title {
  font-size: 13px;
  color: #333;
}

.dash-list-snippet {
  margin: 6px 0 0;
  font-size: 12px;
  color: #666;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Flagged item preview modal — match linked Q/N/M; iframe fills full modal body */
.dash-preview-overlay {
  position: fixed;
  inset: 0;
  z-index: 10050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.45);
  box-sizing: border-box;
}

.dash-preview-modal {
  width: min(1380px, 100%);
  height: calc(100vh - 48px);
  max-height: calc(100vh - 48px);
  overflow: hidden;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.28);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.dash-preview-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border-top: 5px solid;
  border-bottom: 1px solid #e6e6e6;
  flex: 0 0 auto;
}

.dash-preview-type {
  margin: 0 0 4px;
  color: #6d6e71;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

.dash-preview-header h3 {
  margin: 0;
  color: #231f20;
  font-size: 20px;
  line-height: 1.25;
  word-break: break-word;
}

.dash-preview-close {
  width: 34px;
  height: 34px;
  border: 1px solid #d6d6d6;
  border-radius: 50%;
  background: #fff;
  color: #6d6e71;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.dash-preview-close:hover {
  color: #231f20;
  background: #f6f6f6;
}

.dash-preview-frame-wrap {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  background: #fff;
}

.dash-preview-footer {
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 12px 18px;
  border-top: 1px solid #e6e6e6;
  background: #fafafa;
}

.dash-preview-goto {
  appearance: none;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 10px 18px;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  line-height: 1.2;
}

.dash-preview-goto:hover:not(:disabled) {
  filter: brightness(0.95);
}

.dash-preview-goto:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.dash-preview-frame {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
  background: #fff;
}

.dash-preview-error {
  padding: 40px 24px;
  text-align: center;
  color: #6d6e71;
  font-size: 14px;
  font-style: italic;
}

.dash-preview-fade-enter-active,
.dash-preview-fade-leave-active {
  transition: opacity 0.2s ease;
}

.dash-preview-fade-enter,
.dash-preview-fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .dash-preview-overlay {
    padding: 10px;
  }

  .dash-preview-modal {
    height: calc(100vh - 20px);
    max-height: calc(100vh - 20px);
  }

  .dash-preview-header h3 {
    font-size: 16px;
  }
}

.dash-comment-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.dash-tag {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 8px;
  letter-spacing: 0.04em;
}
.dash-tag--q { background: #d7ebff; color: #1a90ff; }
.dash-tag--n { background: #fce2b5; color: #c77800; }
.dash-tag--m { background: #ffd6d8; color: #ed1c24; }

.dash-comment-date {
  font-size: 11px;
  color: #999;
  margin-left: auto;
}

.dash-comment-text {
  margin: 0;
  font-size: 13px;
  color: #444;
  line-height: 1.5;
}

.dash-feedback-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.dash-feedback-list li {
  padding: 10px 0;
  border-bottom: 1px solid #eee;
}

.dash-feedback-list li:last-child {
  border-bottom: none;
}

.dash-feedback-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  margin-bottom: 4px;
}

.dash-feedback-preview {
  margin: 0;
  font-size: 13px;
  color: #555;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
}

@media (max-width: 1100px) {
  .dash-overview-grid {
    grid-template-columns: 1fr;
  }

  .dash-overview-grid--score {
    width: 100%;
  }

  .dash-sub-row {
    grid-template-columns: 1fr;
  }

  .dash-sub-section-status,
  .dash-sub-section-action {
    justify-content: flex-start;
    min-height: auto;
    padding: 0 2px 4px;
  }

  .dash-subscribed-wrap {
    width: 100%;
    justify-content: space-between;
  }

  .dash-subscribed-text {
    align-items: flex-start;
    text-align: left;
  }
}

@media (max-width: 1024px) {
  .dashboard-shell {
    grid-template-columns: 1fr;
  }
  .dash-overview-grid,
  .dash-info-row,
  .dash-list-grid {
    grid-template-columns: 1fr;
  }
  .dash-mini-stats {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
